// src/lib/branchTransfers.js
// Inter-branch transfers, both fulfilment modes. Issues #371 and #425,
// Session 79.6, built against gate revision 2:
// docs/gates/Stede_BranchTransfer_Gate_371.html — 27 decisions, approved.
//
// ── What this is, in plain words ──
//
// A big job lands at Houston and the vinyl is sitting at Dallas. Separately,
// every branch needs stakes and frames and only Houston makes them, out of raw
// steel, to order. Those two are the SAME KIND OF THING in every way that
// matters here — internal, no money, ask → agree → send → receive — and they
// differ only in whether the sending branch takes it off a shelf or makes it
// first. So they share one document and one set of screens, and Stede works out
// which is which on its own (gate D15).
//
// ── The one rule to understand before changing anything ──
//
// THE MODE COMES FROM THE CATEGORY TREE, NOT FROM THE ITEM'S VENDOR.
//
// Revision 1 of the gate derived it per item: "this item's vendor is an
// internal branch, so that branch makes it." STAGING DISPROVED THAT IN ABOUT A
// MINUTE. Dibond White, Dibond Black and MDO all carry Houston vendor links and
// Houston manufactures none of them — they are exactly the materials that
// should transfer off a shelf. Under the old rule, asking for Dibond would have
// routed as "Houston will make this."
//
// Colby's own sentence settled it: "stakes and frames are the two categories
// Houston manufactures." That is ONE FACT ABOUT TWO TREES instead of
// twenty-five facts about items. A category names its manufacturing branch and
// everything beneath it inherits, so DT Stakes, Push Stakes, Std Frames and
// anything added next year are covered without anybody remembering — and Dibond
// and MDO need no special case at all, because their category names no maker.
//
// The vendor link then carries COST and nothing else (D17), which genuinely is
// per item: a 60in stake does not cost what a 24in one does. Two facts, two
// homes, no overlap, where the original conflated them.

import { supabase } from './supabase';
import { computeNewAverageCost } from './materialReceive';
import {
  sendNotification,
  getRequestApproverIds,
  getStockAuditRecipientIds,
} from './notify';
// #514, Session 79.7. Mail composition lives in its own module for the same
// reason materialRequestEmails.js does -- this file moves stock and writes
// bells, and address-book lookups are a different job. Locked rule 14.
import { notifyTransferRequested, notifyTransferClosedShort } from './branchTransferEmails';
import { orEveryoneWhoCanDecide } from './decisionMakers';
import { canWithdrawTransfer, knowsVerb } from './canApprove';
import { transferLinkId } from './deepLink';

export const TRANSFER_STATUSES = [
  'requested', 'approved', 'in_transit', 'received',
  'denied', 'cancelled', 'closed_short',
  // #517 / D56, Session 79.10 -- THE SECOND ENDING. `closed_short` is goods
  // that LEFT and vanished; this is a balance nobody is going to send, where
  // nothing left and no money is lost. Two endings, two words, because calling
  // both "short close" puts a loss report in front of a manager for a truck
  // that was never loaded.
  'closed_unfilled',
  // #528, Session 79.10 -- the supplying branch's turn. `requested` now means
  // "waiting on the ASKING branch"; this means "waiting on the branch that
  // would supply it".
  'awaiting_confirmation',
];

// ─────────────────────────────────────────────────────────────────────────────
// PURE LOGIC — no database, no React. Everything below this line is testable
// without a network, and it is where every gate decision that can be got wrong
// silently actually lives.
// ─────────────────────────────────────────────────────────────────────────────

// Walk UP the category tree to the nearest ancestor that names a manufacturing
// branch. Gate D15 as amended.
//
// Returns { mode, branchId, departmentId, viaCategoryId }.
//   mode 'make'  — some ancestor names a maker; that branch manufactures it
//   mode 'stock' — nothing in the chain does; it comes off a shelf
//
// Walking up rather than reading the root means a SUB-category can override its
// parent if D&R ever needs it to, and nothing has to be repeated on the eight
// categories that exist today.
//
// The cycle guard is not defensive padding: `parent_id` is a self-reference with
// no database constraint preventing a loop, and a loop here would hang the
// request sheet rather than fail it. `check_category_depth()` makes one
// unlikely; it does not make one impossible.
export function resolveFulfilment(categories, categoryId) {
  const none = { mode: 'stock', branchId: null, departmentId: null, viaCategoryId: null };
  if (!Array.isArray(categories) || categoryId == null) return none;

  const byId = new Map(categories.map(c => [String(c.id), c]));
  const seen = new Set();
  let node = byId.get(String(categoryId));

  while (node) {
    if (seen.has(String(node.id))) return none;   // a cycle: treat as shelf stock
    seen.add(String(node.id));

    if (node.manufactured_at_branch_id != null) {
      return {
        mode: 'make',
        branchId: node.manufactured_at_branch_id,
        departmentId: node.manufacturing_department_id ?? null,
        viaCategoryId: node.id,
      };
    }
    node = node.parent_id == null ? null : byId.get(String(node.parent_id));
  }
  return none;
}

// #565 D3, Session 79.15i -- WHICH BRANCH TO ASK, and it is #558's rule LIFTED
// rather than rebuilt, which is what that decision asks for.
//
// Colby's rule: "technically that is the branch with the most material to
// spare... if branch a and b both have 100 on hand and a's reorder point is 50
// and b's is 75 then branch a has the most to spare." So SURPLUS, not pile.
//
// IT LIVED AS TWO LOCAL FUNCTIONS INSIDE InventoryStockHome.js, whose own
// comment says it was written because the answer "was previously computed twice
// and two copies of a rule drift in exactly one half". The request sheet then
// became a THIRD place that needed it - and was still ranking by the raw pile,
// so the sheet and Stock Home could recommend different branches for the same
// item. Exported here so there is one.
//
// THE MIXED CASE IS COLBY'S RULING at 79.15b and is deliberate: a branch with NO
// reorder point is ranked on its whole on-hand and may out-rank one that has set
// a floor. It reads wrong at first glance and the alternatives are worse -
// treating an unset floor as "unknown" hides real stock, and guessing one
// invents a number nobody entered.
//
// #721, Session 79.22b.5.1b -- AND WHAT THE BRANCH HAS ALREADY PROMISED COMES
// OFF FIRST (`o.promised`, from promisedFromShelf() below). San Antonio holding
// 45 that has said yes to 20 for Dallas cannot spare those 20 to anybody else,
// and every "to spare" figure and every "which branch to ask" ranking reads this
// one function -- so they all take it off together. Absent, it is zero, and
// every figure is exactly what it was.
export function surplusOf(o) {
  if (!o) return 0;
  const qty = (Number(o.qty) || 0) - (Number(o.promised) || 0);
  return o.reorderPoint == null ? qty : qty - Number(o.reorderPoint);
}

// #721, Session 79.22b.5.1b -- WHAT EACH BRANCH HAS ALREADY PROMISED OFF ITS
// SHELF, keyed `${item_id}:${branch_id}` like every stock map in the module.
// APPROVED BY COLBY at 79.22b.5 (the drawing) and 79.22b.5.1b (R1).
//
// "Already promised" is stock a branch has said yes to on another branch's
// transfer and has not yet put on a truck -- SAP's confirmed outbound
// commitment, which its available-to-promise check subtracts. Per agreed item,
// it is what is READY and not yet SHIPPED:
//   - a shelf item is ready the moment it is agreed (553 D1, #577), so this is
//     everything agreed less what has left;
//   - a made item is ready as it is signed off, and sign_off_production() puts
//     it on the maker's own shelf -- so frames made for Dallas and waiting for
//     their truck are on Houston's shelf and promised, not free.
// Stock leaves only at the origin when a load sets off (`qty_shipped` counts
// exactly those loads), so ready less shipped is what still stands there.
//
// Only `approved` and `in_transit`: a request still waiting for a yes is not a
// promise, and a finished or called-off transfer -- `closed_unfilled` included,
// "nothing more coming" -- promises nothing more. A branch's own-shelf run is
// never a promise to anybody (#582 as amended). Refused items are skipped.
export const PROMISING_STATUSES = ['approved', 'in_transit'];
export function promisedFromShelf(transfers) {
  const out = {};
  for (const t of (Array.isArray(transfers) ? transfers : [])) {
    if (!t || !PROMISING_STATUSES.includes(t.status) || t.from_branch_id == null) continue;
    if (isOwnShelf(t)) continue;
    for (const l of (t.branch_transfer_items || t.items || [])) {
      if (l?.item_id == null || isRefusedLine(l)) continue;
      const left = (Number(l.qty_ready) || 0) - (Number(l.qty_shipped) || 0);
      if (left <= 0) continue;
      const key = `${l.item_id}:${t.from_branch_id}`;
      out[key] = (out[key] || 0) + left;
    }
  }
  return out;
}

// Greatest surplus wins; the raw pile breaks a tie so the order is stable rather
// than dependent on row order, and the name breaks THAT tie for the same reason.
// A list that reorders itself between renders reads as a bug.
export function rankBranchesBySurplus(others, nameOf) {
  if (!Array.isArray(others) || !others.length) return [];
  return [...others].sort((a, b) =>
    (surplusOf(b) - surplusOf(a))
    || ((Number(b.qty) || 0) - (Number(a.qty) || 0))
    || String(nameOf ? nameOf(a.branchId) : a.branchId)
        .localeCompare(String(nameOf ? nameOf(b.branchId) : b.branchId))
  );
}

export function bestBranchBySurplus(others, nameOf) {
  return rankBranchesBySurplus(others, nameOf)[0] || null;
}

// #615, Session 79.22b -- HOW MANY A LOW ROW IS SHORT BY, in one place. Stock
// Home's pathway sentences and the request sheet both need it, and the sheet
// was reading the wrong number: reorder_qty, the size Dallas would BUY. null
// when the row has no reorder point - there is no floor to be short of.
export function rowShortBy(row) {
  if (!row || row.reorder_point == null) return null;
  return Math.max(Number(row.reorder_point) - (Number(row.qty_on_hand) || 0), 0);
}

// What `others` says one branch can SPARE (#558's surplusOf), or null when that
// branch is not in the list.
export function spareAt(others, branchId) {
  const o = (Array.isArray(others) ? others : []).find(x => String(x.branchId) === String(branchId));
  return o ? surplusOf(o) : null;
}

// #615 -- WHAT THE REQUEST SHEET PROPOSES. APPROVED BY COLBY at 79.22a: "your
// recommendation on #615 is approved." A shortage of 20 opened as a request
// for 200 - reorder_qty - to a branch that could spare 50. Now it is the
// SHORTFALL, capped at what the sending branch can SPARE: gate 565 callout B's
// "the number a requester needs is what they can actually get".
//
// The cap is the PROPOSAL, never a limit. The box stays editable upward, so
// gate 550/551 D13's partial transfer survives: somebody who wants to ask for
// more than a branch can spare may type it.
//
// `why` names the case, so the sheet can say which one it is:
//   short          the branch can spare all of it
//   capped         it can spare some; this proposes that much
//   none_to_spare  it holds some but sits at or below its own floor - no number,
//                  because any number would take it below that floor
//   make           made to order: no shelf to cap against (D15 hides it anyway)
//   no_floor       no reorder point here, so nothing to be short of - no number
//   not_short      at the floor, not below it - no number
export function proposedAskQty({ shortBy, mode, spare }) {
  if (shortBy == null) return { qty: null, why: 'no_floor' };
  const need = Math.max(Number(shortBy) || 0, 0);
  if (need === 0) return { qty: null, why: 'not_short' };
  if (mode === 'make') return { qty: need, why: 'make' };
  if (spare == null) return { qty: need, why: 'short' };
  const canSpare = Number(spare) || 0;
  if (canSpare <= 0) return { qty: null, why: 'none_to_spare' };
  return canSpare < need ? { qty: canSpare, why: 'capped' } : { qty: need, why: 'short' };
}

// Who should send this, and can they? Returns { mode, fromBranchId, reason }.
//
// In MAKE mode the sending branch is decided FOR the requester - nobody picks
// it, and no on-hand figure is shown. NOT because nothing is on a shelf:
// #515 gave Houston one and #522 was logged because this claim had become
// false. It is hidden because "0 at Houston" beside a request for two
// hundred reads as a refusal - D15/D16's reason, which still holds. In STOCK

// mode the requester picked a branch that has the stock, and asking your own
// branch to send to itself is refused here as well as by the database.
export function chooseSendingBranch({ fulfilment, requesterBranchId, pickedBranchId }) {
  if (fulfilment?.mode === 'make') {
    // #565 D1, Session 79.15i -- REACTIVE LATERAL TRANSSHIPMENT, which is
    // Colby's own case and the term he adopted for it.
    //
    // Dallas needs 60 stakes. Houston makes them and has none standing; San
    // Antonio has 90 on a shelf it does not need. Until this line, San Antonio
    // was not merely un-recommended, it was UNASKABLE: for anything in a made
    // category this function ignored the picked branch entirely and forced the
    // maker. His words: "this is a real life situation all of the branches face
    // and need to be able to request."
    //
    // THE MAKER IS STILL THE DEFAULT AND THAT IS D2. Asking the maker is
    // planned replenishment; asking a sister branch is the reactive exception,
    // so it is available rather than automatic. A caller that picks nobody gets
    // exactly today's behaviour, which is what makes this safe for the common
    // path - acceptance line 2 exists to prove D1 cost it nothing.
    //
    // A PICKED BRANCH THAT IS THE MAKER IS STILL A MAKE REQUEST. The mode is
    // about WHO SUPPLIES, and the maker supplying is the make path whether the
    // requester arrived there by default or by choosing it.
    //
    // `fulfilment.branchId != null` is FIRST so the "no manufacturing branch is
    // named" guard below still wins. Without it, String(null) is the string
    // "null", which no real id equals, so a category with no maker plus a picked
    // branch would silently take this path instead of reporting the gap.
    if (fulfilment.branchId != null
        && pickedBranchId != null
        && String(pickedBranchId) !== String(fulfilment.branchId)) {
      if (String(pickedBranchId) === String(requesterBranchId)) {
        return { mode: 'stock', fromBranchId: null, reason: 'a branch cannot transfer to itself' };
      }
      // Mode `stock` because a HOLDER is supplying it off a shelf - nobody is
      // manufacturing anything for this request. What it is made OF does not
      // change, and that is why `made_at_branch_id` is stamped separately:
      // costToTravel() still values it at the standard cost. D11.
      return { mode: 'stock', fromBranchId: pickedBranchId, reason: null };
    }
    if (fulfilment.branchId == null) {
      return { mode: 'make', fromBranchId: null, reason: 'no manufacturing branch is named for this category' };
    }
    if (String(fulfilment.branchId) === String(requesterBranchId)) {
      // Houston asking Houston to make stakes is not a transfer, it is a
      // production run for its own shelf. Out of scope by D24's reasoning and
      // refused rather than silently producing a same-branch document the
      // database would reject anyway.
      return { mode: 'make', fromBranchId: null, reason: 'your own branch makes these — no transfer is needed' };
    }
    return { mode: 'make', fromBranchId: fulfilment.branchId, reason: null };
  }
  if (pickedBranchId == null) {
    return { mode: 'stock', fromBranchId: null, reason: 'choose which branch is sending' };
  }
  if (String(pickedBranchId) === String(requesterBranchId)) {
    return { mode: 'stock', fromBranchId: null, reason: 'a branch cannot transfer to itself' };
  }
  return { mode: 'stock', fromBranchId: pickedBranchId, reason: null };
}

// Gate D5 — in transit is DERIVED, never stored. Shipped minus received, on
// open lines only.
//
// A second column on inventory_stock would teach every existing reader a state
// it has never had to know, and Stede's stock table is deliberately one quantity
// per item per branch. #512 is the follow-up that puts this on a second screen,
// hosted Oct 1 rather than now, precisely so a brand-new derivation gets ONE
// consumer before it gets two.
export function inTransitQty(lines) {
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((sum, l) => {
    const shipped = Number(l.qty_shipped) || 0;
    const received = Number(l.qty_received) || 0;
    const open = shipped - received;
    return sum + (open > 0 ? open : 0);
  }, 0);
}

// Gate D18 — what a short close costs, in units and in dollars.
//
// At the cost that TRAVELLED with the goods (unit_cost_at_transfer), captured
// at ship time, so a price change afterwards cannot rewrite what a past
// shipment was worth. A line whose cost was never recorded contributes units
// but no value, which is honest: two rolls lost is still two rolls even when
// nobody can say what they cost.
//
// ── #677, Session 79.22b.4.3 — THE COST IS READ FROM THE TRUCKS ─────────────
//
// It read the TRANSFER LINE's unit_cost_at_transfer, and nothing has written
// that since split loads: the cost lives on branch_transfer_load_items, per
// truck, because two trucks can carry one item at two costs (G9: 250 at 5.25
// and 150 at 9.00). So every current write-off said "some had no recorded
// cost" while the load, the ledger and the load sheet all knew it was $4.00.
//
// ONE RULE, READ BY THE PAGE, THE BELL AND THE EMAIL. The bell used the figure
// close_branch_transfer_short() returns, which reads the same dead column, so
// the page and the bell were wrong in the same way because they were two copies
// of one rule. Colby's ruling at the open (option A): worked out here, once, in
// the app — #240, the database owns counts and the app owns cost. That
// function's returned figure is no longer read by anything.
//
// WHAT WAS LOST IS WHAT LEFT MINUS WHAT ARRIVED, per item, each at its own
// truck's cost — the same trucks sync_transfer_line_rollups() counts: loaded
// at the transfer's ORIGIN for shipped, counted in at its DESTINATION for
// received, undone trucks ignored. So the figure is exactly the gap the stock
// history shows between the two branches, to the cent. Found proving it on
// HOU-TRF-00008: three trucks at $3.40, $4.00 and $4.00, two short on the
// last; an average by quantity said $7.70, the ledger says $77.00 left and
// $69.00 arrived. A hub's onward leg is neither origin nor destination
// traffic in the middle, so goods are never counted twice.
//
// If ANY of an item's trucks carried it with no cost, the item is unpriced — a
// figure built from some of its trucks would state a number nobody recorded.
//
// No trucks for an item at all is a transfer from before split loads (4, 9,
// 12, 18 on the test database), whose cost did live on the line; it still
// reads there.
export function shortfall(lines, { loads = null, fromBranchId = null, toBranchId = null } = {}) {
  const rows = Array.isArray(lines) ? lines : [];
  const trucks = new Map();
  const tally = (key) => {
    if (!trucks.has(key)) trucks.set(key, { left: 0, arrived: 0, unpriced: false });
    return trucks.get(key);
  };
  for (const load of Array.isArray(loads) ? loads : []) {
    if (!load || load.status === 'undone') continue;
    const leaves = fromBranchId != null && String(load.from_branch_id) === String(fromBranchId);
    const lands = toBranchId != null && String(load.to_branch_id) === String(toBranchId);
    if (!leaves && !lands) continue;
    for (const li of load.branch_transfer_load_items || []) {
      const t = tally(String(li.item_id));
      const cost = li.unit_cost_at_transfer == null ? NaN : Number(li.unit_cost_at_transfer);
      if (!Number.isFinite(cost)) { t.unpriced = true; continue; }
      if (leaves) t.left += (Number(li.qty_loaded) || 0) * cost;
      if (lands) t.arrived += (Number(li.qty_arrived) || 0) * cost;
    }
  }
  let units = 0;
  let value = 0;
  let anyUnpriced = false;
  for (const l of rows) {
    const shipped = Number(l.qty_shipped) || 0;
    const received = Number(l.qty_received) || 0;
    const missing = shipped - received;
    if (missing <= 0) continue;
    units += missing;
    const t = trucks.get(String(l.item_id));
    if (t) {
      if (t.unpriced) anyUnpriced = true;
      else value += Math.max(0, t.left - t.arrived);
      continue;
    }
    const cost = l.unit_cost_at_transfer == null ? NaN : Number(l.unit_cost_at_transfer);
    if (!Number.isFinite(cost)) anyUnpriced = true;
    else value += missing * cost;
  }
  // To the cent, so $3.40 x 5 cannot surface as $7.999999.
  return { units, value: Math.round(value * 100) / 100, anyUnpriced };
}

// WHAT COST TRAVELS WITH THE GOODS — gate D16 and D17, and this function exists
// because BOTH were unimplemented in the screen while both were correctly
// implemented in the arithmetic.
//
// Found by Colby watching a real end-to-end run at Session 79.6. The ship screen
// read the cost off the transfer LINE, and a line's cost is null until the
// moment of shipping — so it was always null, in both modes. Every test passed,
// because the tests check the blending calculation and the calculation was never
// handed a number. A live run is the only thing that could have caught it.
//
// The two answers come from different places, and that is the whole point:
//
//   MADE for this order   → the internal vendor's price on vendor_items.
//                           D17: Houston never BOUGHT a stake, so there is no
//                           average to carry. A standard cost is what every
//                           system studied offers when there is no bill of
//                           materials or labour capture.
//   OFF A SHELF           → the SENDING branch's own average cost.
//                           D16: "it cost what it cost" is true of the company
//                           and false of a branch, because avg_unit_cost is
//                           keyed on item AND branch.
//
// Null is a legitimate answer and must stay one: an unknown price leaves the
// receiver's average alone rather than corrupting it, which is exactly why 79.5
// left stake prices EMPTY on staging rather than inventing placeholders.
// EACH ANSWER FALLS BACK TO THE OTHER, and that is not belt-and-braces — it is
// a hole found live at Session 79.6, minutes after the first fix.
//
// Houston records a reserve run of 300 stakes for its own shelf. `inventory_stock.
// avg_unit_cost` is not touched by that, correctly, because #240's boundary says
// the database owns quantity and the application owns cost — and no screen
// exists yet to do the blending.
//
// Then Dallas asks for 100 and Houston ships FROM THAT SHELF. Preferred source
// is the sender's average, which is null, so the goods arrive valued at nothing
// — the exact defect that had just been fixed, wearing the other mode's clothes.
//
// The fallback is honest in both directions rather than defensive. For a thing a
// branch MAKES, the standard cost is what it costs whether it came off the rack
// or the saw. For a thing a branch BUYS, its own average is the truth even when
// somebody has typed a standard cost somewhere. Preference, then the other, then
// null.
// ── AMENDED AT SESSION 79.8 BY GATE DECISION D45. READ BEFORE CHANGING IT ──
//
// IT KEYS ON `fulfilment_mode`, NOT ON `sourced_by`, AND THE TWO ARE NOT
// INTERCHANGEABLE. Session 79.6 separated them at real cost because conflating
// them was a live defect, and then this function asked the wrong one.
//
//   fulfilment_mode  WHO CAN SUPPLY THIS — read from the category tree when the
//                    transfer is created. A frame is a made thing. Permanent.
//   sourced_by       HOW IT WAS FILLED — the sender's answer at ship time, off
//                    the rack or off the saw. It decides whether a production
//                    row gets written, and nothing else.
//
// WHY THE DISTINCTION DECIDES THE MONEY. A frame is worth what a frame costs to
// make. Whether Houston cut it that morning or had it on a rack does not change
// what it is worth — that is what a standard cost MEANS, and it is what both
// systems studied do:
//
//   * SAP, price control S: a plant transfer leaves the material's price
//     UNCHANGED. The receiving plant values at its own standard and any
//     difference goes to a price-difference account. The cost does not travel,
//     because both ends already know it.
//   * Oracle: a standard-costing receiving organisation needs a variance
//     account for inter-org receipts, for exactly the same reason.
//
// WHAT THIS ACTUALLY CHANGED, STATED NARROWLY SO NOBODY OVER-READS IT. The old
// keying gave a different answer ONLY when both numbers were present: a made
// item, shipped off a shelf, at a branch that also had an average for it. That
// took the average; it now takes the cost to make. Every fallback is unchanged.
//
// IT IS NOT WHY A SHIPMENT ONCE TRAVELLED AT NULL. That was both inputs being
// null at once — no branch average (structural, below) and no cost to make set
// on the item. The fallback already covers the case where the cost exists, and
// the test named for it was green throughout.
//
// A BRANCH'S AVERAGE IS NULL FOREVER FOR ANYTHING IT MAKES, deliberately —
// gate D46. `record_production()` does not touch `avg_unit_cost` and must not
// start: an average is the instrument for what you BUY and a standard cost for
// what you MAKE, and blending the two is what produced this confusion.
//
// The fallbacks stay, because a usable number beats a null — and null still
// beats a zero, which is a claim the thing was free and flows into valuation
// looking correct.
// #565 D11, Session 79.15i -- THE ARGUMENT CHANGED FROM `fulfilmentMode` TO
// `madeAtBranchId`, AND THE REASON IS THE WHOLE OF D1.
//
// `fulfilment_mode` used to answer two questions at once - "is this a made
// thing" and "who is supplying it" - and they were the same question only
// because a made item could ONLY ever come from its maker. D1 lets a branch ask
// a sister branch that merely HOLDS a made item, so a stake off San Antonio's
// shelf is now mode `stock` and still a thing Houston makes.
//
// Ask the old question and that stake is valued at SAN ANTONIO'S AVERAGE
// instead of what it costs to make - silently, nothing erroring, flowing into
// inventory valuation looking correct. The stamped column is the only thing
// that still answers "is this a made thing" after D1.
//
// BOTH CALL SITES MOVE IN THE SAME COMMIT. A site still passing
// `fulfilmentMode` hands this `madeAtBranchId: undefined`, and
// `undefined != null` is FALSE, so every made item silently takes the holder's
// average. That is locked rule 8's failure shape on a non-permission function,
// and the D45 test below reads the screen's SOURCE precisely because a swap
// back would be invisible to every value-based assertion.
export function costToTravel({ madeAtBranchId, senderAvgCost, standardCost }) {
  const usable = v => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const made = madeAtBranchId != null;
  const preferred = made ? usable(standardCost) : usable(senderAvgCost);
  if (preferred != null) return preferred;
  return made ? usable(senderAvgCost) : usable(standardCost);
}

// How long the maker has, in days, from today to the date the asker needs it.
//
// Gate: the approval screen reads "Approving commits Houston to manufacturing
// 200. Needed by Sep 20 — 12 days." The number is the point — it is what lets
// the person committing the company judge whether it is achievable BEFORE they
// press Approve, rather than discovering it when Houston misses.
//
// Negative is a real answer, not an error: somebody can ask for a date that has
// already passed, and saying "3 days ago" is more use than hiding it.
export function leadTimeDays(neededBy, from = new Date()) {
  if (!neededBy) return null;
  const target = new Date(`${String(neededBy).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  return Math.round((target - start) / 86400000);
}

// The quick options offered beside the date field, and they are DIFFERENT per
// mode because the two modes have different time physics.
//
// Colby, Session 79.6: "making 100 frames cant happen today, cant happen
// tomorrow and could maybe happen this week."
//
// Shelf horizons are shipping horizons — somebody puts a box in a truck. Make
// horizons are capacity horizons. Offering Today/Tomorrow on a production run
// means every request says "this week" because it is the only option that is
// not a lie, and a column where every row reads the same is one nobody reads.
export const SHELF_HORIZONS = [
  { label: 'Today', days: 0 },
  { label: 'Tomorrow', days: 1 },
  { label: 'This week', days: 7 },
];

export const MAKE_HORIZONS = [
  { label: 'This week', days: 7 },
  { label: 'Next week', days: 14 },
  { label: 'In 3 weeks', days: 21 },
  { label: 'No rush', days: 42 },
];

// Gate D16 — cost travels, and the receiver's average is recalculated on
// arrival.
//
// "It cost what it cost" is true of the COMPANY and false of a BRANCH, because
// inventory_stock keys avg_unit_cost on item AND branch. Dallas sending 20 at
// $10 into Houston's 5 at $12 gives $10.40 over 25 units. The sender's average
// does not change and is not touched: removing units at the average leaves it
// exactly where it was.
//
// This calls the RECEIVE path's own routine rather than restating the formula.
// A null cost leaves the receiver's average alone.
export function receivedAverageCost({ currentQty, currentAvg, receivedQty, unitCostAtTransfer }) {
  return computeNewAverageCost(
    Number(currentQty) || 0,
    currentAvg == null ? null : Number(currentAvg),
    Number(receivedQty) || 0,
    unitCostAtTransfer == null ? null : Number(unitCostAtTransfer),
  );
}

// The number always renders with its direction — gate D9 and D20, and this is
// Colby's condition on approving the sender's-prefix rule rather than a
// nicety.
//
// HOU-TRF-00001 names only the branch it LEFT. On a receiving branch's screen
// that reads as somebody else's document, so the direction rides along
// everywhere the number appears: "HOU-TRF-00001 · Houston → Dallas".
export function transferLabel(transfer, branchNamesById) {
  if (!transfer) return '';
  const num = transfer.transfer_number || 'Not yet numbered';
  return `${num} · ${transferRoute(transfer, branchNamesById)}`;
}

// THE DIRECTION ALONE: "Houston → Dallas". #675's finding at 79.22b.5.5b -- a
// transfer is numbered when it is approved, so every ask on Awaiting
// confirmation is "Not yet numbered", and the desk's Next button names such a
// card by its route instead (Colby's R1). One copy of the rule, which the label
// above reads too.
export function transferRoute(transfer, branchNamesById) {
  if (!transfer) return '';
  const from = branchNamesById?.[String(transfer.from_branch_id)] || 'a branch';
  // #580 part a, gate 553 F2 and X1 line 9 -- a request a branch made for its
  // own shelf has no direction to show, and "Houston → Houston" must appear
  // nowhere. Every screen reads its label from here, so none composes it.
  if (isOwnShelf(transfer)) return `For ${from}'s own shelf`;
  const to = branchNamesById?.[String(transfer.to_branch_id)] || 'a branch';
  return `${from} → ${to}`;
}

// #580 part a, gate 553 D21, Session 79.21b -- A BRANCH MAKING FOR ITS OWN
// SHELF. Both ends are the same branch; the database allows that only for a
// make request by the branch that makes the item. Nothing is loaded and
// nothing is counted in, so every tab that deals in trucks leaves it out.
export function isOwnShelf(transfer) {
  return !!transfer
    && transfer.from_branch_id != null
    && String(transfer.from_branch_id) === String(transfer.to_branch_id);
}

// #577, Session 79.21b -- A REFUSED ITEM IS SETTLED BY ITS REFUSAL.
//
// ONE PLACE, because the defect was seven places: makeProgress(),
// productionQueue(), lineProgress's totals, shipmentStage(), owedToBranch(),
// isLoadable() and isUnfillable() all counted an item the supplying branch had
// said no to as still to make, still to come and still awaited. Its ready count
// stays zero forever, so it sat in Houston's build queue as work nobody would
// do, and it held the transfer open on every tracker. Every total now starts
// from this list. The refused item itself still renders, with its reason, on
// the screens that show lines one by one.
export function isRefusedLine(line) {
  return line?.line_status === 'refused';
}

// ── #578, Session 79.21b — WHAT THE REFUSAL PANEL SUGGESTS ──────────────────
//
// Colby's rule on #567 (rule 3), and his ruling at 79.21a on the case it never
// covered: the default on every route is the pack of THAT route's supplier -
// the maker's own on "Ask Houston to make these", the preferred outside vendor's
// on "Send to Purchasing", and none on "Try another branch", where no vendor
// supplies it. And when the pack is SMALLER than the need, the need rounded UP
// to whole packs: 250 in cases of 10 suggests 250, 50 in pallets of 250 suggests
// 250. The panel used one number per item from whichever row was preferred, and
// suggested an outside vendor's case of 10 for a need of 250 on the make route.
//
// `packs` is [{ qty, unit, preferred, internal, branchId, vendorName }].
export function routePack(packs, route, makerBranchId) {
  const rows = (Array.isArray(packs) ? packs : []).filter(p => Number(p?.qty) > 0);
  if (route === 'make') {
    const own = rows.filter(p => p.internal && makerBranchId != null
      && String(p.branchId) === String(makerBranchId));
    return own.find(p => p.preferred) || own[0] || null;
  }
  if (route === 'buy') {
    const outside = rows.filter(p => !p.internal);
    return outside.find(p => p.preferred) || outside[0] || null;
  }
  return null;
}

export function suggestedRouteQty(need, pack) {
  const n = Number(need) || 0;
  const size = Number(pack?.qty) || 0;
  if (!(size > 0) || n <= 0) return n;
  return Math.ceil(n / size) * size;
}

// "pallets of 250", "cases of 10", "packs of 6" - the supplier's own word.
export function packPhrase(pack) {
  if (!pack || !(Number(pack.qty) > 0)) return null;
  const unit = String(pack.unit || 'pack').trim().toLowerCase();
  const plural = unit.endsWith('s') ? unit : `${unit}s`;
  return `${plural} of ${Number(pack.qty)}`;
}

// Which branches could send the refused items instead, and how many each can
// SPARE - its pile above its own reorder point, #558's rule (surplusOf above).
// A branch qualifies only if it has some to spare of EVERY refused item, since
// one request to it carries them all. The refusing branch and the asking branch
// are never offered (565 D15). Biggest spare first.
//
// `stockByKey` is keyed `${item_id}:${branch_id}` with qty_on_hand and
// reorder_point, which is how the Transfers page already holds stock.
//
// #721 -- `promisedByKey` (promisedFromShelf(), same keys) comes off each
// branch's spare, so a branch whose spare is all promised is not offered. Each
// entry with some promised also carries `promised`, the single item's figure, for the panel's
// "20 already promised" line; with several items it is not drawn (drawing E).
export function branchesWithSpare({ stockByKey, itemIds, branchIds, excludeBranchIds = [], promisedByKey = {} }) {
  const ids = (Array.isArray(itemIds) ? itemIds : []).map(String);
  const skip = new Set((excludeBranchIds || []).filter(v => v != null).map(String));
  const out = [];
  if (!ids.length) return out;
  for (const b of (Array.isArray(branchIds) ? branchIds : []).map(String)) {
    if (skip.has(b)) continue;
    let least = Infinity;
    let promised = 0;
    for (const item of ids) {
      const row = stockByKey?.[`${item}:${b}`];
      const owed = Number(promisedByKey?.[`${item}:${b}`]) || 0;
      const spare = row ? surplusOf({ qty: row.qty_on_hand, reorderPoint: row.reorder_point, promised: owed }) : 0;
      least = Math.min(least, spare);
      promised += owed;
    }
    // `promised` only when there is some: with nothing promised the entry is
    // exactly what it always was.
    if (least > 0) out.push(promised > 0 ? { branchId: b, spare: least, promised } : { branchId: b, spare: least });
  }
  return out.sort((a, b) => b.spare - a.spare);
}

export function agreedLines(lines) {
  return (Array.isArray(lines) ? lines : []).filter(l => !isRefusedLine(l));
}

// ── #580, Session 79.21b — WHAT A LOW-STOCK ROW OFFERS ──────────────────────
//
// Gate 550/551's pathway model (D1-D19), brought to its APPROVED COPY (F2) as
// Colby ruled for #580 part (b), and gaining the one pathway that gate left to
// gate 553: MAKE THESE OURSELVES (553 D21-D27, #580 part a).
//
// PURE and here rather than in the page, which is what 550/551 F5 asked for and
// the first build did not do: every decision that can be got wrong silently
// lives in this list, and a list inside a component cannot be tested.
//
// Ordered by priority. `exception` marks D14's "buy a made item anyway" line:
// NEVER counted as a pathway (553 T, 550/551's #580 amendment), so it can never
// be the only thing a tap reaches. `carriesException` marks a pathway whose own
// sheet shows that line itself — the own-shelf sheet does (553 F2), the transfer
// sheet does not — which is what decides whether a single tap may skip the
// chooser without stranding the exception (acceptance line A1).
//
// `others` is [{ branchId, qty, reorderPoint }] for the OTHER branches.
//
// `viewerBranchId` is the branch of the PERSON looking, which is not always the
// row's: a manager may open another branch's Stock Home. Gate 553 B4 offers
// Make these ourselves only where the category tree names the person's OWN
// branch, and the database refuses anybody else - so a Dallas manager looking at
// Houston's frames is told why rather than handed a button that fails. Omitted,
// the person is taken to be at the row's branch.
export function stockPathways({ row, categories, others = [], branchNameOf = id => String(id), viewerBranchId = null } = {}) {
  const f = resolveFulfilment(categories, row?.inventory_items?.inventory_categories?.id);
  const isMade = f.mode === 'make';
  const makerIsHere = isMade && String(f.branchId) === String(row?.branch_id);
  const makerElsewhere = isMade && !makerIsHere;
  const maker = isMade ? branchNameOf(f.branchId) : null;
  const viewerElsewhere = viewerBranchId != null && String(viewerBranchId) !== String(row?.branch_id);

  // #615 -- one definition of the shortfall, shared with the request sheet.
  const need = rowShortBy(row);

  // The maker is left out of the shelf option when Make is offered: two options
  // pointing at the same branch ask a question already answered.
  const shelf = (Array.isArray(others) ? others : [])
    .filter(o => !(makerElsewhere && String(o.branchId) === String(f.branchId)));
  const best = bestBranchBySurplus(shelf, branchNameOf);

  const out = [];
  if (makerIsHere) {
    out.push({
      key: 'make_own', short: 'Make these',
      // "Ourselves" is only true for somebody at the maker.
      label: viewerElsewhere ? `${maker} makes these for its own shelf` : 'Make these ourselves',
      detail: 'We make this here. It goes on our own shelf when it is done — nothing is bought and nothing ships.',
      primary: true, carriesException: true,
      // The sheet opens and answers the press with the reason (#492's rule),
      // instead of a form the database would refuse.
      unavailable: viewerElsewhere
        ? `Only someone at ${maker} can ask ${maker}'s shop to make these for its own shelf.`
        : null,
    });
  }
  if (makerElsewhere) {
    out.push({
      key: 'make', short: `Ask ${maker}`, label: `Ask ${maker} to make these`,
      detail: `D&R manufactures this. ${maker} builds it and ships it — nothing is bought.`,
      primary: true,
      // #582 -- which branch this asks, so an open ask to it can be found.
      fromBranchId: f.branchId,
    });
  }
  if (best) {
    const from = branchNameOf(best.branchId);
    // D13: a shelf that cannot cover the need is still offered, and says so.
    // With no reorder point there is no need to measure against, so the
    // sentence says only what is there.
    //
    // #721 (drawing A, approved at 79.22b.5.1b) -- the shelf is judged on what
    // is NOT already promised, and the promise is named beside the pile, so
    // "enough" is never said of sheets another branch is already getting.
    const promised = Number(best.promised) || 0;
    const free = Math.max(best.qty - promised, 0);
    const shelf = promised > 0
      ? `${best.qty} on the shelf at ${from}, ${promised} already promised`
      : `${best.qty} on the shelf at ${from}`;
    let detail;
    if (!need) detail = `${shelf}. Nothing is bought.`;
    else if (free >= need) detail = `${shelf} — enough to cover all ${need}. Nothing is bought.`;
    else detail = `${shelf} — ${need - free} short of what you need. You can ask for these and still request the rest.`;
    out.push({
      key: 'transfer', short: `Ask ${from}`, label: `Ask ${from} to send some`, detail,
      // D5: never primary, because it is true this morning and false this
      // afternoon; the chooser styles it as recommended (callout 7).
      primary: false, recommended: true, fromBranchId: best.branchId, qty: best.qty,
    });
  }
  if (!isMade) {
    out.push({
      key: 'buy', short: 'Request', label: 'Request from Purchasing',
      detail: best
        ? `Purchasing buys these from a vendor. Use this if you need more than ${branchNameOf(best.branchId)} can spare.`
        : 'Purchasing buys these from a vendor.',
      primary: out.length === 0,
    });
  } else {
    // D14 — a made item can still be bought, as a named exception, never a peer.
    out.push({
      key: 'buy', short: 'Buy', label: 'Buy from a vendor instead?',
      lead: makerIsHere && !viewerElsewhere ? 'We make these.' : `${maker} makes these.`,
      exception: true, primary: false,
    });
  }
  return out;
}

// The pathways a tap may reach — D14's exception never counts (550/551 D7).
export function realPathways(pathways) {
  return (Array.isArray(pathways) ? pathways : []).filter(p => !p.exception);
}

// What a single tap on the row does: open one sheet directly, or the chooser.
// One real pathway goes straight to it — unless an exception exists that only
// the chooser can show, because the sheet the pathway opens does not carry it
// (acceptance line A1: Dallas asking Houston keeps "buy it anyway" reachable).
// #613, Session 79.22b -- gate 550/551 D20, APPROVED BY COLBY, Sep 21: at a
// desk, a row whose click opens more than its buttons show says so with a ›.
// Exactly when the click opens the chooser (tapTarget below) AND the chooser
// carries D14's exception - today, an item another branch makes. A row whose
// buttons already show every way to get it opens a chooser holding the same
// things, so it gets no mark: nothing is hidden, so nothing is signalled.
export function rowOpensMore(pathways) {
  const all = Array.isArray(pathways) ? pathways : [];
  return !!tapTarget(all).chooser && all.some(p => p.exception);
}

export function tapTarget(pathways) {
  const all = Array.isArray(pathways) ? pathways : [];
  const real = realPathways(all);
  // No real pathway cannot happen today (a made item always offers Make, a
  // bought one always offers Request) - and if it ever did, the chooser shows
  // the exception as the question it is rather than a tap landing on it alone.
  if (real.length !== 1) return { chooser: true };
  const stranded = all.some(p => p.exception) && !real[0].carriesException;
  return stranded ? { chooser: true } : { open: real[0].key };
}

// #582, Session 79.22b -- APPROVED BY COLBY, Sep 21: "yes i like the more
// descriptive buttons" - and CORRECTED BY HIM the same hour, because the
// first build put "again" on every button of a stepped-down row. Houston's
// row said "Ask Austin again" when the only thing coming was Houston's own
// run and nobody had asked Austin. His rule: "It should only say ask austin
// again if one of the branches has already asked austin for those items."
//
// So each word is true of ITS OWN route:
//   Ask X again   a branch already has an open ask to X for this item
//   Make more     our own shop is already making some (553 D25, unchanged)
//   Request more  something is already on its way, so this asks for more
//                 than what is coming - it claims no earlier request
export function pathwayButtonLabel(p, { steppedDown = false, makingHere = false, asked = false } = {}) {
  if (!p) return null;
  if (p.key === 'make_own') return makingHere ? 'Make more' : p.short;
  if (p.key === 'buy') return steppedDown ? 'Request more' : p.short;
  return asked ? `${p.short} again` : p.short;   // Ask Austin again
}

// The open asks for items, keyed `${itemId}:${supplyingBranchId}`, each a
// list of { toBranchId, qty } summed per asking branch. OPEN means still
// being agreed, waiting on the supplier, agreed, or on the road; a refused
// line and a finished transfer are not an ask any more.
export const OPEN_ASK_STATUSES = ['requested', 'awaiting_confirmation', 'approved', 'in_transit'];
export function openAsksFor(transfers) {
  const out = {};
  for (const t of (Array.isArray(transfers) ? transfers : [])) {
    if (!t || !OPEN_ASK_STATUSES.includes(t.status) || t.from_branch_id == null) continue;
    // #582 amended, Session 79.22b.4.8 -- A BRANCH'S OWN-SHELF RUN IS NOT AN
    // ASK. HOU-TRF-00016 is Houston's shop making 10 frames for Houston's own
    // shelf; keyed by its supplier it made Dallas's button read "Ask Houston
    // again" - "Houston has already asked for 10." - for an ask nobody made. At
    // Houston that run already reads Make more (553 D25), so nothing is lost.
    if (isOwnShelf(t)) continue;
    for (const l of (t.branch_transfer_items || [])) {
      if (l?.item_id == null || l.line_status === 'refused') continue;
      const qty = Number(l.qty_requested) || 0;
      if (qty <= 0) continue;
      const key = `${l.item_id}:${t.from_branch_id}`;
      const list = (out[key] = out[key] || []);
      const mine = list.find(a => String(a.toBranchId) === String(t.to_branch_id));
      if (mine) mine.qty += qty; else list.push({ toBranchId: t.to_branch_id, qty });
    }
  }
  return out;
}

// The hover on an "again" button, in Colby's words: "Dallas has already
// asked for 10".
export function alreadyAskedSentence(asks, branchNameOf = id => String(id)) {
  return (Array.isArray(asks) ? asks : [])
    .map(a => `${branchNameOf(a.toBranchId)} has already asked for ${a.qty}.`)
    .join(' ');
}

// The row's ONE control on a phone names the outcome (550/551 Screen 1). Once
// the shop is already making some for this shelf, Make these steps down to
// Make more (553 D25), so the next person does not ask again - and an ask
// already open to the branch it names reads "again" (#582, as corrected).
export function rowControlLabel(pathways, { makingHere = false, steppedDown = false, askedFor = () => false } = {}) {
  const real = realPathways(pathways);
  if (real.length > 1) return `${real.length} ways to get these`;
  return pathwayButtonLabel(real[0], { makingHere, steppedDown, asked: !!(real[0] && askedFor(real[0])) });
}

// ── #684, Session 79.22b.4 — WHICH SHEET A REQUEST OPENS, FROM EVERY DOOR ───
//
// APPROVED BY COLBY, Sep 25: "#684: approved as drawn. Build it."
//
// Stock Home asks the category tree before it opens anything (#550, #580). Six
// other doors did not -- Request materials, the item page's three buttons, the
// Request restock screen's own list, the cart's Search and Scan, the QR label,
// and Stock Home's Request several -- so a frame Houston makes opened a
// PURCHASE request from every door but one row. This is the one question every
// door now asks, answered from the item record rather than from the person,
// which is what Oracle and SAP both do: "a requestor may not know the item
// source type" (Oracle Purchasing, Entering Requisition Lines).
//
//   buy        nobody makes it -- the purchase sheet, unchanged
//   ask_maker  another branch makes it -- Ask {maker} to make these
//   make_own   the person's own branch makes it -- Make these ourselves
//
// A person whose branch is not known is never taken to be the maker: asking
// the maker is the safe reading, and the database refuses a same-branch ask.
export function requestRoute({ categories, categoryId, viewerBranchId = null } = {}) {
  const f = resolveFulfilment(categories, categoryId);
  if (f.mode !== 'make') return { route: 'buy', makerBranchId: null, makerDepartmentId: null };
  const own = viewerBranchId != null && String(f.branchId) === String(viewerBranchId);
  return { route: own ? 'make_own' : 'ask_maker', makerBranchId: f.branchId, makerDepartmentId: f.departmentId };
}

// What a request line proposes before anybody types. A line going to a maker
// proposes the SHORTFALL -- #615's ruling for every ask ("the number you are
// short, not the number you would buy"); a purchase proposes the buy size,
// unchanged. Neither is ever blank, because the cart has no empty box: with no
// shortfall to go by, the buy size -- or 1 -- is what the cart always offered.
export function requestLineQty({ route, shortBy = null, reorderQty = null } = {}) {
  const buySize = Number(reorderQty) > 0 ? Number(reorderQty) : 1;
  if (route === 'buy') return buySize;
  return Number(shortBy) > 0 ? Number(shortBy) : buySize;
}

// ONE REQUEST, SPLIT BY WHERE EACH LINE GOES -- the widget's D, E and F.
//
// Every line is routed on its own, from its own item record, the way Oracle
// lets one requisition "mix inventory and supplier sourced requisition lines"
// and turns each kind into its own document. A line the person has chosen to
// buy anyway (`buyAnyway`, D14's exception applied to one line) goes to
// Purchasing whatever makes it.
//
// Asks are grouped by MAKER, which is RequestTransferSheet's D69 key for a made
// item: one branch cannot commit another, so two makers are two requests.
// `makeOwn` lines are returned separately because they cannot be sent from a
// list at all -- asking our own shop is its own sheet and its own write
// (request_own_shelf_make), with a ready-by date only its confirmer may set.
export function splitRequestLines(lines, { categories, viewerBranchId = null } = {}) {
  const out = { buy: [], asks: [], makeOwn: [] };
  for (const l of (Array.isArray(lines) ? lines : [])) {
    if (!l) continue;
    const r = requestRoute({ categories, categoryId: l.categoryId, viewerBranchId });
    if (l.buyAnyway || r.route === 'buy') { out.buy.push(l); continue; }
    if (r.route === 'make_own') { out.makeOwn.push(l); continue; }
    let g = out.asks.find(a => String(a.makerBranchId) === String(r.makerBranchId));
    if (!g) {
      g = { makerBranchId: r.makerBranchId, makerDepartmentId: r.makerDepartmentId, lines: [] };
      out.asks.push(g);
    }
    g.lines.push(l);
  }
  return out;
}

function itemCount(n) {
  return `${n} item${n === 1 ? '' : 's'}`;
}

// ── #717, Session 79.22b.6d — A BELL NAMES WHAT IS ASKED FOR ────────────────
//
// The approved drawing (docs/audits/79.22b.6d/APPROVED_DRAWING_*.html). A
// transfer has no number until the supplying branch confirms it, so every bell
// sent before then that named it by transferLabel() said "Not yet numbered ·
// Houston → Dallas" - or, counting rows, "make 1 item" for 60 stakes. Eight
// bells did; all eight now name what was asked for, by these two helpers.
//
// "60 Stake Push 60" · "60 Stake Push 60 and 8 Frame WA 24x18" · "60 Stake
// Push 60 and 2 more items" (Colby's choice over "+2 more"). Lines are
// { name, qty } or the raw { qty_requested, inventory_items: { name } }. With
// any name missing it falls back to the count - never "undefined" (#514).
export function itemsPhrase(lines) {
  const xs = (Array.isArray(lines) ? lines : []).filter(Boolean);
  if (xs.length === 0) return null;
  const named = xs.map(l => {
    const name = l.name || l.inventory_items?.name;
    if (!name) return null;
    const raw = l.qty ?? l.qty_requested;
    const qty = raw == null || raw === '' ? null : Number(raw);
    return qty == null || Number.isNaN(qty) ? name : `${qty} ${name}`;
  });
  if (named.some(n => !n)) return itemCount(xs.length);
  if (named.length <= 2) return andList(named);
  return `${named[0]} and ${named.length - 1} more items`;
}

// What a transfer asks for, read back from the database for a bell, with the
// requester's name the supplying branch's email also needs. Best-effort, the
// way the email always read it: a failed read leaves the bell its plainer
// sentence and never blocks a decision that has already been made.
export async function readTransferLines(transferId) {
  try {
    const { data: full } = await supabase
      .from('branch_transfers')
      .select('users!branch_transfers_requested_by_fkey(full_name), branch_transfer_items(qty_requested, line_status, inventory_items(name, uom))')
      .eq('id', transferId)
      .maybeSingle();
    return {
      requesterName: full?.users?.full_name || null,
      lines: (full?.branch_transfer_items || []).map(l => ({
        name: l.inventory_items?.name, qty: l.qty_requested, uom: l.inventory_items?.uom, line_status: l.line_status,
      })),
    };
  } catch (err) {
    console.error('[#717] could not read the transfer for its bell', { transferId, err });
    return { requesterName: null, lines: [] };
  }
}

// The same for the purchase request a refusal was sent on to (the buy route).
export async function readRequestLines(requestId) {
  try {
    const { data } = await supabase
      .from('material_requests')
      .select('material_request_items(qty_requested, inventory_items(name, uom))')
      .eq('id', requestId)
      .maybeSingle();
    return (data?.material_request_items || []).map(l => ({ name: l.inventory_items?.name, qty: l.qty_requested }));
  } catch (err) {
    console.error('[#717] could not read the purchase request for its bell', { requestId, err });
    return [];
  }
}

// E, F and G -- the bells to the PERSON WHO ASKED. A request with a number is
// named by it, exactly as before; one without is named by what it was, the
// withdraw bells' pattern (79.21b): "Your request to Houston for 60 Stake Push
// 60". Two asks open at Houston can now be told apart.
export function yourRequestLead(transfer, branchNames = {}, lines = []) {
  if (transfer?.transfer_number) return transferLabel(transfer, branchNames);
  const supplier = branchNames?.[String(transfer?.from_branch_id)] || 'the other branch';
  const what = itemsPhrase(lines);
  if (isOwnShelf(transfer)) {
    return `Your request to make ${what || 'these'} for ${supplier}'s own shelf`;
  }
  return what ? `Your request to ${supplier} for ${what}` : `Your request to ${supplier}`;
}

// #589, the approved drawing -- what the approval bell says about WHEN. One
// date: "promised Fri, Sep 25". Items given different dates: the span, as My
// requests prints it. None: "no date promised", and only on a made-to-order
// request - a shelf transfer is never asked for a date, so saying none was
// given would be noise on every one. `promisedDates` is { [item_id]: date }.
export function promisedClause({ mode, confirmedDate = null, promisedDates = null } = {}) {
  const dates = [confirmedDate, ...Object.values(promisedDates || {})]
    .filter(Boolean).map(String).sort();
  if (dates.length === 0) return mode === 'make' ? ' — no date promised' : '';
  const first = promisedDayLabel(dates[0]);
  const last = promisedDayLabel(dates[dates.length - 1]);
  if (!first) return '';
  return first === last || !last ? ` — promised ${first}` : ` — promised ${first} to ${last}`;
}

// F -- SAID BEFORE ANYTHING IS SENT, D69's rule widened from branches to
// destinations. Null when everything goes to Purchasing, which is what the cart
// always did and needs no announcing. `count` is how many requests Send makes.
export function requestSendSummary(split, branchNameOf = id => String(id)) {
  if (!split || !Array.isArray(split.asks) || split.asks.length === 0) return null;
  const parts = split.asks.map(g => `${itemCount(g.lines.length)} for ${branchNameOf(g.makerBranchId)} to make`);
  if (split.buy.length) parts.push(`${itemCount(split.buy.length)} for Purchasing to buy`);
  const count = split.asks.length + (split.buy.length ? 1 : 0);
  const approves = count === 1 ? 'it' : count === 2 ? 'both' : `all ${count}`;
  return {
    count,
    sentence: `This sends ${count} request${count === 1 ? '' : 's'}: ${parts.join(', ')}. Your manager approves ${approves}.`,
  };
}

// D -- the words on a line, so each one says where it is going. Null for a plain
// purchase line in a list with nothing made on it: an all-purchase cart looks
// exactly as it always has.
export function requestLineTag({ route, makerName = null, buyAnyway = false } = {}) {
  if (route === 'ask_maker') {
    return buyAnyway
      ? `Buying from a vendor. ${makerName} makes these.`
      : `${makerName} makes this, so it goes to ${makerName}`;
  }
  if (route === 'make_own') return buyAnyway ? 'Buying from a vendor. We make these.' : 'We make this here';
  return 'Purchasing buys this';
}

// The refusal a Send answers with while a line is one only our own shop can
// take (#492: a control that refuses a press says why).
export function makeOwnBlockedSentence(lines) {
  const names = (Array.isArray(lines) ? lines : []).map(l => l.itemName).filter(Boolean);
  if (!names.length) return null;
  return `We make ${names.join(' and ')} here. Use Make these ourselves on its line, or Buy from a vendor instead.`;
}

// Gate D3 and D14 — who may approve.
//
// canApproveTransfer lives in canApprove.js with the material-request rule, per
// locked rule 8. This is only the "is it in a state that can be approved"
// half, which is a property of the DOCUMENT rather than of the person.
// #528, Session 79.10 -- THIS MEANS THE SUPPLY STAGE, NOT THE FIRST ONE.
//
// A transfer now passes through TWO approvals: its own branch agrees to the ask
// (`requested`), then the supplying branch agrees to supply it
// (`awaiting_confirmation`). This predicate is the second, because it is what
// the Transfers page is built around -- that page is the supplying branch's
// screen and the first stage happens in the Requests inbox.
//
// `isEndorsable` below is the first. They are deliberately two names: one
// predicate with a flag would be read wrong at exactly one of its two call
// sites, and that call site decides who sees an Approve button.
export function isApprovable(transfer) {
  return !!transfer && transfer.status === 'awaiting_confirmation';
}

// The FIRST stage -- the asking branch agreeing that its own people may ask.
export function isEndorsable(transfer) {
  return !!transfer && transfer.status === 'requested';
}

export function isShippable(transfer) {
  return !!transfer && transfer.status === 'approved';
}

export function isReceivable(transfer) {
  return !!transfer && transfer.status === 'in_transit';
}

// WHERE A TRANSFER IS, IN THE THREE NUMBERS THAT DECIDE IT — #553, Session
// 79.15e, D1 to D3.
//
// `toMake` is what nobody has built yet. `readyToLoad` is what is standing on
// the rack with no truck under it. A transfer legitimately has BOTH at once —
// 60 still to make, 40 ready — and Colby took that consequence explicitly: it
// is the opposite of today's defect, where both tabs show the same 100 for the
// same reason.
//
// `qty_ready` is NOT NULL with a default of 0 in the database, so the COALESCE
// here is for a row that arrived from somewhere other than a fresh select —
// a fixture, or an optimistic update. Every test in this repository asserts
// what a function does with a VALUE, and all seven of 79.6's defects were
// values that never arrived.
export function makeProgress(lines) {
  // #577 -- agreed items only. A refused item is never "to make".
  const rows = agreedLines(lines);
  return rows.reduce((acc, l) => {
    const requested = Number(l.qty_requested) || 0;
    const ready = Number(l.qty_ready) || 0;
    const shipped = Number(l.qty_shipped) || 0;
    acc.requested += requested;
    acc.ready += ready;
    acc.shipped += shipped;
    acc.toMake += Math.max(requested - ready, 0);
    acc.readyToLoad += Math.max(ready - shipped, 0);
    // #713, 682 D11 -- what was MADE, only when every line's runs were read
    // (`made_by_runs`, transferLoads.js withMadeCounts()); otherwise null, and
    // the whole-transfer line reads as before.
    acc.made = acc.made == null || l.made_by_runs == null ? null : acc.made + (Number(l.made_by_runs) || 0);
    return acc;
  }, { requested: 0, ready: 0, shipped: 0, toMake: 0, readyToLoad: 0, made: 0 });
}

// D2 — "To make" is anything with something still to build.
//
// THE `fulfilment_mode === 'make'` TEST IS DELETED, NOT KEPT ALONGSIDE, and
// that is the decision rather than a tidy-up. Under D1 a shelf transfer is set
// to `ready = requested` the moment it is approved — the frames really are on
// the rack — so it can never enter this queue and no code path needs to ask
// whether it was made to order. Keeping the mode test as a second net would be
// a second rule that can disagree with the first, and a redundant guard that
// drifts is worse than no guard because it reads as protection. One rule, one
// place: this project has already paid for a screen-width boundary stated 45
// times across 28 files in two forms that disagree at the boundary.
//
// `in_transit` is in the status list and must be. Houston makes 40 of 100 and
// a truck takes them; the transfer is in transit and sixty frames are still to
// build. Filtering to `approved` alone would empty the queue the moment the
// first partial load left the yard.
//
// Gate D24's original point still stands and D6 records the overturn precisely:
// this is a FILTERED VIEW, not an eighth status. D24 was right that no status
// was needed and was never asked whether a QUANTITY was.
export function productionQueue(transfers, branchId) {
  return (Array.isArray(transfers) ? transfers : [])
    .filter(t =>
      ['approved', 'in_transit'].includes(t.status) &&
      String(t.from_branch_id) === String(branchId) &&
      makeProgress(t.branch_transfer_items).toMake > 0)
    .sort((a, b) => new Date(a.requested_at || 0) - new Date(b.requested_at || 0));
}

// ─────────────────────────────────────────────────────────────────────────────
// DATABASE OPERATIONS
//
// Every multi-table write goes through a Postgres function called with
// supabase.rpc(), per locked rule 7. Ship and receive stay AS THE CALLER, and
// that is the enforcement rather than a default: inventory_transactions' insert
// rule ties every stock row to the caller AND to the caller's own branch, so
// the sender can only write their leg and the receiver theirs. Converting
// either to elevated rights silently removes the two-step guarantee (gate D4).
// ─────────────────────────────────────────────────────────────────────────────

// The fabricating department's manager — gate D27, and make requests ONLY.
//
// A sibling of getStockAuditRecipientIds() rather than of
// getRequestApproverIds(), for #500's own reason: the approver lookup also
// requires can_approve_requests, which is a switch about authorising SPENDING.
// A fabrication manager who cannot approve a purchase still runs the shop
// floor, so gating this on a spending permission would silently drop the one
// recipient it exists to reach.
async function fabricationManagerIds(branchId, departmentId) {
  if (!branchId || departmentId == null) return [];
  const ids = await getStockAuditRecipientIds(branchId, departmentId);
  return ids;
}

// #591 and #593, Session 79.21b -- ONE CALL, AND THE DATABASE FILLS IN WHO
// AND WHICH BRANCH MAKES IT.
//
// This used to save the request and then its items as two separate writes from
// the browser. A connection dropped between them left an empty request waiting
// in a manager's inbox, and the retry raised a second one (#591, locked rule 7).
// create_branch_transfer() writes both or neither.
//
// It also used to take the maker, the maker's department, the requester and
// their department from the screen -- and the request sheet worked the maker out
// for every group and never passed it (#593). Every transfer raised through the
// app carried no maker, so a refusal silently hid "Ask Houston to make these"
// and a made item off a sister branch's shelf was valued as bought. The function
// now reads the person from the login and the maker from the category tree
// (item_maker(), the walk record_production() makes), so no screen can forget
// either one. `name` and `uom` on a line ride along for the email only.
//
// #583 -- THE ASKING BRANCH'S APPROVERS ARE TOLD FIRST, because since #528 the
// request waits on them before the supplying branch sees it at all. The same
// people a material request tells (getRequestApproverIds with the requester's
// department). Houston hears when it becomes Houston's turn, in
// endorseTransfer(), and not before.
export async function createTransfer({
  fromBranchId, toBranchId, mode, lines,
  neededBy = null, workOrderRef = null, notes = null, branchNames = {},
  // #514, Session 79.7 -- FOR THE EMAIL ONLY.
  requesterName = null,
}) {
  if (!fromBranchId || !toBranchId) return { error: new Error('Choose which branch to ask.') };
  if (String(fromBranchId) === String(toBranchId)) return { error: new Error('A branch cannot send to itself.') };
  if (!Array.isArray(lines) || lines.length === 0) return { error: new Error('Add at least one item.') };

  const { data: transfer, error } = await supabase.rpc('create_branch_transfer', {
    p_from_branch_id: fromBranchId,
    p_to_branch_id: toBranchId,
    p_mode: mode,
    p_lines: lines.map(l => ({
      item_id: l.item_id,
      qty_requested: l.qty_requested,
      notes: l.notes || null,
    })),
    p_needed_by: neededBy || null,
    p_work_order_ref: workOrderRef || null,
    p_notes: notes || null,
  });
  if (error) return { error };
  // error === null is not proof a write happened (#504): the function returns
  // the row it wrote, and anything else is treated as nothing saved.
  if (!transfer || transfer.id == null) {
    return { error: new Error('That did not save. Nothing was sent — try again.') };
  }

  await tellAskingBranch(transfer, { lines, requesterName, branchNames });

  return { transfer };
}

// ── A PURCHASE REQUEST SENT AS A TRANSFER — #614, gate 371 D38-D40 ────────────
//
// APPROVED BY COLBY, Sep 21: "i approve D38, 39, & 40 on gate 371." Jennifer,
// ordering Dallas's approved request, types Houston - one of our own branches -
// and sends the ticked lines to Houston as a transfer instead. The database
// does all of it in one call (convert_request_to_transfer): the permission,
// the split, the transfers, the closed request. This tells the people.
//
// Each transfer tells the stage it STARTS at (D39): already agreed by the
// request's approver -> the supplying branch, exactly as agreeing does; no
// approver on record -> the asking branch, exactly as asking does. Then the
// person who asked and the manager who approved are told once each (D40) -
// an informing bell, so no fallback to everybody (#650's rule).
export function isValidTransferWorkOrder(ref) {
  return /^\d{6}(-\d+)?$/.test(String(ref || ''));
}

export function sentAsTransferMessage({ requestNumber, supplierName, endorsed, askingName }) {
  const to = supplierName || 'another branch';
  return `Purchasing sent ${requestNumber} to ${to}. ${to} supplies these, so nothing is bought. `
    + (endorsed ? `${to} confirms next.` : `${askingName || 'Your branch'} agrees first, then ${to} confirms.`);
}

export async function sendRequestAsTransfer({ requestId, fromBranchId, requestItemIds, requesterName = null, branchNames = {} }) {
  if (!requestId || !fromBranchId) return { error: new Error('Choose which branch supplies it.') };
  if (!Array.isArray(requestItemIds) || requestItemIds.length === 0) return { error: new Error('Tick the lines to send.') };

  const { data, error } = await supabase.rpc('convert_request_to_transfer', {
    p_request_id: requestId,
    p_from_branch_id: fromBranchId,
    p_request_item_ids: requestItemIds,
  });
  if (error) return { error };
  // error === null is not proof a write happened (#504).
  if (!data || !Array.isArray(data.transfers) || data.transfers.length === 0) {
    return { error: new Error('That did not save. Nothing was sent \u2014 try again.') };
  }

  // The rows the bells read, and their lines for the email. Best-effort: the
  // transfer exists whatever happens here, and a failed read still bells.
  const ids = data.transfers.map(t => t.id);
  let rows = data.transfers;
  try {
    const { data: full, error: readErr } = await supabase
      .from('branch_transfers')
      .select('id, transfer_number, status, from_branch_id, to_branch_id, fulfilment_mode, needed_by, manufacturing_department_id, requested_by_department_id, made_at_branch_id, branch_transfer_items ( qty_requested, inventory_items ( name, uom ) )')
      .in('id', ids);
    if (!readErr && Array.isArray(full) && full.length) rows = full;
  } catch (err) {
    console.error('[#614] could not read the new transfers back', { ids, err });
  }

  for (const t of rows) {
    if (t.status === 'awaiting_confirmation') {
      await tellSupplyingBranch(t, { neededBy: t.needed_by || null, branchNames });
    } else {
      const lines = (t.branch_transfer_items || []).map(l => ({
        name: l.inventory_items?.name, qty_requested: l.qty_requested, uom: l.inventory_items?.uom,
      }));
      await tellAskingBranch(t, { lines, requesterName, branchNames });
    }
  }

  const told = [...new Set([data.requested_by, data.approved_by].filter(Boolean))];
  const supplierName = branchNames?.[String(fromBranchId)] || null;
  await sendNotification(
    told,
    `Sent to ${supplierName || 'another branch'} as a transfer`,
    sentAsTransferMessage({
      requestNumber: data.request_number,
      supplierName,
      endorsed: !!data.endorsed,
      askingName: branchNames?.[String(rows[0]?.to_branch_id)] || null,
    }),
    'material_request_sent_as_transfer',
    'inventory',
    'transfers',
    ids[0],
  );

  return { result: data, transfers: rows };
}

// ── THE TWO STAGE BELLS, shared ─────────────────────────────────────────────
//
// #614, Session 79.22b -- MOVED out of createTransfer() and endorseTransfer()
// unchanged, because a transfer sent from a purchase request (gate 371 D38-D40)
// must tell exactly the people an ordinary one tells, at the stage it starts:
// already agreed by its branch -> the supplying branch, as endorsing does; no
// approver on record -> the asking branch, as creating does. Two copies of a
// bell drift in exactly one half.

// Stage 1: the asking branch's approvers, and their approval email.
export async function tellAskingBranch(transfer, { lines = [], requesterName = null, branchNames = {} } = {}) {
  const fromBranchId = transfer.from_branch_id;
  const toBranchId = transfer.to_branch_id;
  const mode = transfer.fulfilment_mode;
  const neededBy = transfer.needed_by || null;
  const label = transferLabel(transfer, branchNames);
  // #717 A -- what is asked for, not how many rows: "1 item" was 60 stakes,
  // and the label after it was always "Not yet numbered" at this stage.
  const what = itemsPhrase(lines) || itemCount(lines.length);
  const asked = branchNames?.[String(fromBranchId)] || 'another branch';
  // #650 -- a branch with nobody who can agree to it asks the admins and
  // executives instead of telling nobody.
  const { ids: approvers } = await orEveryoneWhoCanDecide(
    await getRequestApproverIds(toBranchId, transfer.requested_by_department_id ?? null),
    toBranchId,
  );
  await sendNotification(
    approvers,
    'A transfer is waiting on your approval',
    `${requesterName || 'Someone on your team'} is asking ${asked} ${mode === 'make' ? 'to make' : 'for'} `
      + `${what}. Approve it in the Requests inbox.`,
    'transfer_requested',
    'inventory',
    // THE REQUESTS INBOX, because that is where this approval happens: the
    // transfer's own page has no first-stage Approve.
    'requests_inbox',
    // #605, Session 79.21c. This was `null`, and the comment above used to end
    // "...and the inbox's link id is the material-request namespace, so a
    // transfer id cannot address it." That was TRUE and it was a reason to fix
    // the namespace, not a reason to send a bell that opens a LIST of
    // everything waiting on somebody.
    //
    // Colby's ruling: the bell opens the transfer it names. His narrowing: the
    // DATABASE ROW ID, because transfer_number is NULL until the supplying
    // branch confirms -- which is exactly the state a transfer is in when this
    // notification is sent, so the number the reader would recognise does not
    // exist yet.
    transferLinkId(transfer.id),
  );

  // #514 / gate D29 -- EMAIL the approver, now the asking branch's (#583).
  // Awaited and never able to abort the request: sendEmail() and every lookup
  // inside notifyTransferRequested() catch their own failures (#497's lesson).
  // Lines are mapped to the email's own shape - `qty`, not `qty_requested` -
  // because handing the raw rows over once put "undefined each" in a real inbox.
  await notifyTransferRequested({
    stage: 'ask',
    recipientBranchId: toBranchId,
    recipientDepartmentId: transfer.requested_by_department_id ?? null,
    label,
    fromBranchName: asked,
    toBranchName: branchNames?.[String(toBranchId)] || 'a branch',
    requesterName,
    mode,
    neededBy,
    lines: (lines || []).map(l => ({ name: l.name, qty: l.qty_requested, uom: l.uom })),
    transferId: transfer.id,
  });
}

// Stage 2: the supplying branch's confirmers, and their email.
export async function tellSupplyingBranch(transfer, { neededBy = null, branchNames = {} } = {}) {
  // The SUPPLYING branch is told, because it has just become their turn. The
  // requester is NOT told: nothing has been agreed for them yet, and a bell
  // saying "approved" at this point would read as their material being on its
  // way. #514's D30 rule — the requester hears when somebody COMMITS and when
  // it LANDS, and this is neither.
  //
  // #583, Session 79.21b -- THIS IS NOW THE ONLY TIME THE SUPPLYING BRANCH
  // HEARS OF IT, so it carries what creation used to: the fabrication manager
  // on a make request (gate D27) and the approver email (D29). Before, Houston
  // was told when the request was raised - before Dallas had agreed to it - and
  // never told if Dallas then said no.
  const isMake = transfer.fulfilment_mode === 'make';
  const suppliers = await getRequestApproverIds(transfer.from_branch_id, null);
  const fabricators = isMake
    ? await fabricationManagerIds(transfer.from_branch_id, transfer.manufacturing_department_id ?? null)
    : [];
  // #650 -- nobody at the supplying branch who can confirm it: ask everybody
  // who may confirm anywhere, rather than nobody.
  const { ids: confirmers } = await orEveryoneWhoCanDecide([...suppliers, ...fabricators], transfer.from_branch_id);
  // #717 D -- one read serves this bell and the email below (locked rule 14).
  // The items the supplying branch is asked about are the AGREED ones: anything
  // the asking branch said it did not need never reaches them (#698).
  const read = await readTransferLines(transfer.id);
  const what = itemsPhrase(agreedLines(read.lines));
  const asker = branchNames?.[String(transfer.to_branch_id)] || 'A branch';
  const supplier = branchNames?.[String(transfer.from_branch_id)] || 'your branch';
  const body = isOwnShelf(transfer)
    ? `${read.requesterName || 'Someone'} asked for ${what || 'material'} for ${supplier}'s own shelf — waiting on you to confirm`
    : `${asker} is asking ${supplier} ${isMake ? 'to make' : 'for'} ${what || 'material'} — waiting on you to confirm`;
  await sendNotification(
    confirmers,
    isMake ? 'Material to make for another branch' : 'A branch is asking for material',
    body,
    'transfer_requested',
    'inventory',
    'transfers',
    transfer.id,
  );

  await emailSupplyingBranch(transfer, { neededBy, branchNames, read });
}

// ── #718, Session 79.22b.4.8 — THE SUPPLYING BRANCH'S APPROVAL EMAIL ────────
//
// Split out of tellSupplyingBranch() so a refusal routed on to another branch
// or the maker sends the SAME email when its new request lands at that
// branch's stage (gate D29, #514, #583). Before, a routed request's new
// supplier got a bell and never the email. Only the email is shared: the
// routing step keeps its own bell, whose words are #717's to redraw, and the
// two paths cannot drift (locked rule 14).
export async function emailSupplyingBranch(transfer, { neededBy = null, branchNames = {}, read = null } = {}) {
  const label = transferLabel(transfer, branchNames);
  // The email needs the items' names and the requester's, which the inbox's
  // row does not carry. Read here, best-effort: a failed read sends a shorter
  // email, never blocks an approval that has already happened. #717 -- the
  // bell that goes with it now reads the same thing first and hands it over,
  // so the two are one read and cannot disagree.
  const got = read || await readTransferLines(transfer.id);
  const requesterName = got.requesterName;
  const emailLines = agreedLines(got.lines).map(l => ({ name: l.name, qty: l.qty, uom: l.uom }));
  await notifyTransferRequested({
    stage: 'supply',
    recipientBranchId: transfer.from_branch_id,
    recipientDepartmentId: null,
    label,
    fromBranchName: branchNames?.[String(transfer.from_branch_id)] || 'a branch',
    toBranchName: branchNames?.[String(transfer.to_branch_id)] || 'a branch',
    requesterName,
    mode: transfer.fulfilment_mode,
    neededBy: neededBy || transfer.needed_by || null,
    lines: emailLines,
    transferId: transfer.id,
  });
}

// ── STAGE 1 — THE ASKING BRANCH AGREES ITS OWN PEOPLE MAY ASK ───────────────
//
// #528, Session 79.10, on Colby's ruling. A team member raises it; their own
// department or branch manager agrees before another branch is asked for
// anything. Until this, a team member at Dallas could commit Houston's shop
// floor to a week of work with nobody at Dallas ever seeing it.
//
// ENDORSE RATHER THAN APPROVE, and the word is doing work. The two stages are
// two different promises made by two different branches, and the screens say so
// in two different sentences. Calling them both "approve" is how somebody reads
// a Dallas endorsement as Houston having agreed to supply.
//
// THE DATE IS THE MANAGER'S TO CONFIRM OR OVERRIDE — Colby: "the team member
// raises the request, and i suppose they can enter a date too but the manager
// approving the request should have the ability to confirm or override the
// date." That is the NEEDED-BY date, what the asker wants. Houston's promised
// date is a separate field set at the next stage; neither overwrites the other,
// which is what lets anybody ask afterwards whether we hit people's dates.
//
// Passing nothing leaves the date alone rather than clearing it, so "approve
// without touching it" is the cheap path and the one most approvals take.
export async function endorseTransfer(transfer, { neededBy = null, branchNames = {} } = {}) {
  const { error } = await supabase.rpc('endorse_branch_transfer', {
    p_transfer_id: transfer.id,
    p_needed_by: neededBy || null,
  });
  if (error) return { error };

  await tellSupplyingBranch(transfer, { neededBy, branchNames });

  return { ok: true };
}

// ── STAGE 1, ITEM BY ITEM — "ONLY SOME OF THESE ARE NEEDED" ─────────────────
//
// #698 half (b), gate 665 D26, Session 79.22b.4.7.1. The asking branch's
// manager answers each item of her own team's request: Needed, or Not needed
// with a reason. ONE CALL, endorse_branch_transfer_lines(), refuses the items
// not needed as the ASKING branch's (#572) and sends the rest on through
// endorse_branch_transfer() itself - or, with nothing needed, is exactly
// today's first-stage Deny through deny_branch_transfer(). The database checks
// who is calling before it writes anything (#616).
//
// `answers` is [{ item_id, answer: 'needed' | 'not_needed', reason }].
//
// THE BELLS FOLLOW WHAT THE DATABASE DID, never the form (gate 665 B3):
//   something needed   the supplying branch is told exactly as Approve tells
//                      it - tellSupplyingBranch(), whose email lists only the
//                      items it is asked about - and, when anything was not
//                      needed, the person who asked is told which and why:
//                      transfer_denied, no new event type (B5), in the words
//                      Colby approved at the open;
//   nothing needed     exactly today's first-stage Deny: the person who asked,
//                      with each item's reason, and no routing bell (#571).
export async function endorseTransferLines(transfer, answers, {
  branchNames = {}, items = {}, neededBy = null,
} = {}) {
  if (!Array.isArray(answers) || answers.length === 0) {
    return { error: new Error('Answer every item before sending.') };
  }
  if (answers.some(a => a.answer === 'not_needed' && !String(a.reason || '').trim())) {
    // Answered here as well as by the database, so the words name the rule
    // rather than surfacing an item number (#499's lesson).
    return { error: new Error('Give a reason for every item that is not needed.') };
  }

  const { data, error } = await supabase.rpc('endorse_branch_transfer_lines', {
    p_transfer_id: transfer.id,
    p_answers: answers.map(a => ({
      item_id: a.item_id,
      answer: a.answer,
      reason: a.answer === 'not_needed' ? String(a.reason || '').trim() : null,
    })),
    p_needed_by: neededBy || null,
  });
  if (error) return { error };
  // A null with no error is the one thing a confirmed write cannot be (#504).
  if (!data) {
    return { error: new Error('That did not save. Nothing has been answered — try again.') };
  }

  const needed = Number(data.needed) || 0;
  const notNeeded = Number(data.not_needed) || 0;
  // #717 G -- the first stage is always before a number, so both bells below
  // name the request by what was asked rather than "Not yet numbered".
  const label = notNeeded > 0
    ? yourRequestLead(transfer, branchNames, (await readTransferLines(transfer.id)).lines)
    : transferLabel(transfer, branchNames);
  const supplier = branchNames?.[String(transfer.from_branch_id)] || 'the other branch';
  // #646's formatter, reused rather than copied: each item not needed, with the
  // reason its manager gave.
  const reasons = refusalReasons(
    answers.map(a => (a.answer === 'not_needed' ? { ...a, answer: 'refused' } : a)), items);

  if (needed > 0) {
    await tellSupplyingBranch(transfer, { neededBy, branchNames });
    if (notNeeded > 0) {
      await sendNotification(
        [transfer.requested_by].filter(Boolean),
        'Partly approved — some items are not needed',
        `${label} — ${needed} of ${needed + notNeeded} items ${needed === 1 ? 'has' : 'have'} gone to ${supplier} to confirm.`
          + `${reasons ? ` Not needed — ${reasons}` : ''}`,
        'transfer_denied',
        'inventory',
        'transfers',
        transfer.id,
      );
    }
  } else {
    await sendNotification(
      [transfer.requested_by].filter(Boolean),
      'Transfer denied',
      `${label} — ${reasons || 'Not needed.'}`,
      'transfer_denied',
      'inventory',
      'transfers',
      transfer.id,
    );
  }

  return { ok: true, status: data.status || null, needed, notNeeded };
}

// What the first-stage answer did, in the counts the DATABASE returned (gate
// 665 B3), for the Requests inbox's message - the words Colby approved at
// 79.22b.4.7.1's open. Null when every item was needed: that is exactly
// Approve, which says nothing either.
export function firstStageAnsweredSentence(result, { supplierName = null, items = {}, answers = [] } = {}) {
  const needed = Number(result?.needed) || 0;
  const notNeeded = Number(result?.notNeeded) || 0;
  if (needed === 0) return 'Refused. The person who asked has been told why.';
  if (notNeeded === 0) return null;
  const refused = (Array.isArray(answers) ? answers : []).filter(a => a?.answer === 'not_needed');
  // #619's rule: ONE item is named, several are "the rest".
  const phrase = refusedItemsPhrase(refused, items);
  return `${needed} of ${needed + notNeeded} sent to ${supplierName || 'the other branch'} to confirm. `
    + `The person who asked has been told why ${phrase} ${phrase === 'the rest' ? "aren't" : "isn't"} needed.`;
}

// "Fri, Sep 26" from a stored 'YYYY-MM-DD'. Read as a LOCAL date: parsing the
// string directly treats it as UTC midnight, which is the evening before in
// Texas, so every promised date would read a day early.
export function promisedDayLabel(date) {
  if (!date) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export async function approveTransfer(transfer, {
  branchNames = {}, requestedBy = null, confirmedDate = null,
  // #580 part a -- who pressed Confirm, so an own-shelf request never tells
  // the person who approved it that it was approved.
  approvedBy = null,
  // #517 / D67, Session 79.10 -- { [item_id]: 'YYYY-MM-DD' }, and only for the
  // lines that DIFFER from the date above.
  promisedDates = null,
} = {}) {
  // The maker's promised date, separate from what the asker needed. The gate's
  // own copy promises this to the requester in so many words -- "They will
  // confirm a date when they accept" -- so leaving it out made that sentence
  // untrue on a shipped screen.
  const { data: number, error } = await supabase
    .rpc('approve_branch_transfer', {
      p_transfer_id: transfer.id,
      p_confirmed_date: confirmedDate || null,
    });
  if (error) return { error };

  // ── D67: A PROMISED DATE PER ITEM ────────────────────────────────────────
  //
  // Colby: different things have different lead times and some of it is already
  // on the shelf. SAP solves this with schedule lines; this takes the simple
  // half -- one date per item, overriding the one date set for the whole ask.
  //
  // `branch_transfers.confirmed_date` STAYS and is what most transfers will
  // ever have. These rows are written only where a line differs, so the common
  // case writes nothing at all and the approver types nothing they need not.
  //
  // DELIBERATELY NOT INSIDE THE APPROVAL, and the reason is what happens when
  // it fails. If it failed inside the approval, the approval would roll back
  // and the requester would be left waiting on a decision that had been made.
  // Out here, a failure degrades to the whole-ask date, which is the answer the
  // screen was already going to give.
  //
  // #750, Session 295 -- ITS OWN STEP NOW. The transfer tables accept changes
  // only through Stede's own steps, so the dates go through
  // set_transfer_promised_dates(), which checks the same "who may confirm" as
  // the approval itself: one call for every dated line, all saved or none.
  let datesError = null;
  const dated = Object.entries(promisedDates || {})
    .filter(([, date]) => date)
    .map(([itemId, date]) => ({ item_id: Number(itemId), promised_date: date }));
  if (dated.length) {
    const { error: dErr } = await supabase.rpc('set_transfer_promised_dates', {
      p_transfer_id: transfer.id,
      p_dates: dated,
    });
    if (dErr) datesError = dErr;
  }

  const withNumber = { ...transfer, transfer_number: number };
  const label = transferLabel(withNumber, branchNames);
  const asker = requestedBy || transfer.requested_by;
  // #580 part a, gate 553 B5 -- a request for the branch's own shelf tells the
  // person who asked and nobody else: the branch's managers are the ones who
  // just approved it. And never the person who pressed Confirm.
  const senders = isOwnShelf(transfer) ? [] : await getRequestApproverIds(transfer.from_branch_id, null);
  const audience = [...new Set([asker, ...senders].filter(Boolean))]
    .filter(id => !(isOwnShelf(transfer) && approvedBy && String(id) === String(approvedBy)));

  // #589 -- THE PROMISED DATE IS THE REASON THIS BELL EXISTS. Gate 371 kept it
  // for the asker so they know when to expect the material, and it said only
  // "was approved". 79.22b.6d, the approved drawing: it also says when NO date
  // was promised on a made request, and gives the span when items differ.
  const when = promisedClause({ mode: transfer.fulfilment_mode, confirmedDate, promisedDates });
  if (audience.length) {
    await sendNotification(
      audience,
      transfer.fulfilment_mode === 'make' ? 'Approved — ready to make' : 'Transfer approved',
      `${label} was approved${when}`,
      'transfer_approved',
      'inventory',
      'transfers',
      transfer.id,
    );
  }
  // The approval STANDS even if a per-item date failed to save -- it did
  // happen, and reporting it as a failure would have the approver press the
  // button again. `datesWarning` lets the screen say the smaller true thing.
  return { transferNumber: number, datesWarning: datesError || null };
}

// DENYING A TRANSFER, AND OFFERING SOMETHING INSTEAD — #559, gate decision D18.
//
// ** WHAT THIS CHANGES FOR A PERSON. ** Dallas asks Houston for 60 stakes.
// Houston says no. Before this, that was the end: the status was written, the
// requester got one notification, the row dropped off every live tab, and the
// stakes were still missing with nobody holding the problem. Now the same
// refusal puts a purchase request in front of the Dallas manager who approved
// the ask, carrying the same quantity, the same work-order number and
// Houston's reason, waiting under Needs approval.
//
// ** OFFERED, NOT ORDERED. ** Colby's correction to the gate, and it is the
// whole design: the denying branch does not know whether the material is still
// needed and the asking branch does. So the successor arrives as something to
// approve rather than something already bought — and it lands with the
// APPROVING MANAGER, not the field user who asked. His words: "requests land
// in approving managers inboxes not in the team member who originally
// requested the transfer."
//
// ** ONE RPC, BECAUSE THREE THINGS MOVE TOGETHER ** — locked rule 7. The
// status, the successor request and its lines. Two client-side writes would
// leave a denied transfer with no successor, which is #559 itself recreated by
// its own fix, or a successor with no lines, which is a purchase request for
// nothing landing in somebody's inbox.
//
// ** ONLY A SUPPLYING-BRANCH REFUSAL RAISES ONE, and that is NOT what the gate
// said. ** #528 established two denial stages: a manager refusing their own
// team member's ask, and a supplying branch declining to supply. D18's own
// reasoning only holds for the second. Built for both, a manager who turned
// down their own team member would have raised a purchase order for the thing
// they had just refused. The database decides this, not the screen.
export async function denyTransfer(transfer, reason, { branchNames = {} } = {}) {
  if (!reason || !String(reason).trim()) {
    return { error: new Error('a reason is required') };
  }

  // The status guard lives in the function now — #215's lesson, taken FOR
  // UPDATE so two managers pressing Deny at once cannot both write. It raises
  // rather than returning zero rows, so the "PostgREST reports no rows changed
  // as SUCCESS" trap (#504) cannot apply to this path at all.
  const { data, error } = await supabase.rpc('deny_branch_transfer', {
    p_transfer_id: transfer.id,
    p_reason: String(reason).trim(),
  });
  if (error) return { error };
  // A null with no error is the one thing a confirmed write cannot be.
  if (!data) {
    return { error: new Error('That did not save. The transfer has not been denied — try again.') };
  }

  // #565 D10, Session 79.15i -- NO SUCCESSOR IS CREATED AT REFUSAL ANY MORE.
  //
  // D18 (79.15g) raised a pending purchase request the instant Deny was pressed.
  // Under D5 and D6 the ASKING branch's manager chooses what happens next, so at
  // the moment of refusal nobody yet knows what kind of document this should be
  // - make, ask another branch, buy, or nothing. Creating a purchase request and
  // converting it later means a document that briefly claims to be something it
  // is not, sitting in the purchasing approval count while it does so.
  //
  // The function still returns these two keys and they are now always null. Kept
  // rather than removed so a caller reading them gets a truthful null instead of
  // undefined, and so the shape survives the production apply ordering: staging
  // ran D18's version first.
  const successorNumber = data.successor_number || null;
  const successorId = data.successor_id || null;

  // D14 -- ONLY A SUPPLYING-BRANCH REFUSAL IS ROUTED. `stage` carries the status
  // the transfer was in when it was refused. 79.15g's narrowing, unchanged: a
  // manager refusing their own team member has decided the material is NOT
  // needed, and putting a routing decision in front of them would invert it.
  const needsRouting = data.stage === 'awaiting_confirmation';

  // THE REQUESTER IS TOLD AND DOES NOT HAVE TO ACT. Their My requests tracker
  // shows the transfer denied and, when there is one, that a purchase request
  // has been raised — so nobody chases something already picked up.
  // THE REQUESTER IS TOLD AND DOES NOT HAVE TO ACT. Their My requests tracker
  // shows the transfer denied.
  //
  // THE SENTENCE ABOUT A PURCHASE REQUEST IS GONE, because under D10 there is no
  // longer a document to name at this moment. Promising one that does not exist
  // is worse than saying nothing - it sends somebody looking for a record
  // nobody has raised, which is the shape #559 was logged for.
  // #717 E -- a denied request never got a number, so it is named by what was
  // asked: "Your request to Houston for 60 Stake Push 60 — None spare".
  const lead = yourRequestLead(transfer, branchNames, (await readTransferLines(transfer.id)).lines);
  await sendNotification(
    [transfer.requested_by].filter(Boolean),
    'Transfer denied',
    `${lead} — ${String(reason).trim()}`,
    'transfer_denied',
    'inventory',
    'transfers',
    transfer.id,
  );

  if (needsRouting) {
    // THE APPROVING MANAGERS OF THE ASKING BRANCH, matching how every other
    // material request is routed — #396, and getRequestApproverIds() is the
    // same call submitRestockRequest() makes, so the two cannot drift apart.
    // The department comes off the transfer, which already recorded it.
    // #650 -- and when the asking branch has nobody who can decide, the admins
    // and executives are asked: a refusal routed to nobody is a dead end again.
    const { ids: approvers } = await orEveryoneWhoCanDecide(await getRequestApproverIds(
      transfer.to_branch_id,
      transfer.requested_by_department_id ?? null,
    ), transfer.to_branch_id);
    // COPY IS THE GATE'S F2 TABLE VERBATIM, and the change from "Denied — buy
    // it instead?" is D15 rather than wording. That title pre-selected BUYING
    // on every refusal, and what a refusal MEANS depends on who gave it: a
    // holder refusing means they are short and the maker is fine, so the maker
    // is the answer. Naming one route in the notification hands the manager a
    // default before they have seen the screen.
    //
    // EVERY INTERPOLATION IS GUARDED. #514 shipped an email reading "undefined
    // each" past an assertion literally named "no rendered email contains the
    // string undefined", because the fixture supplied a shape the real caller
    // never sends. `branch_transfer_items` rides along from TransfersPage's
    // select and is absent elsewhere, so the item phrase degrades to a true
    // sentence rather than to "undefined".
    const refuser = branchNames?.[String(transfer.from_branch_id)] || 'The supplying branch';
    const num = transfer.transfer_number || 'an inter-branch transfer';
    // #698 finding 3, Session 79.22b.4.7.1 -- ONLY WHAT THE SUPPLIER WAS ASKED.
    // An item the asking branch already turned down at the first stage was
    // never put to them, so "could not supply 3 items" would count it (gate
    // 665 X2). Before the press, the items still open are exactly the asked ones.
    const tLines = Array.isArray(transfer.branch_transfer_items) ? agreedLines(transfer.branch_transfer_items) : [];
    const what = tLines.length === 1 && tLines[0]?.qty_requested != null
      ? `${tLines[0].qty_requested} of what you asked for`
      : tLines.length > 1
        ? `${tLines.length} items`
        : 'what you asked for';

    await sendNotification(
      approvers,
      `${refuser} refused ${num}`,
      // #646 amended -- the words exactly as before; only the stop after the
      // quote waits to see whether the reason already ended with one.
      `${refuser} could not supply ${what}: "${String(reason).trim()}"${stopAfterQuote(String(reason).trim())} Choose what happens next.`,
      'transfer_denied_successor',
      'inventory',
      // THE ROUTING SCREEN FOR THAT TRANSFER - B5. It used to point at the
      // requests inbox and at a successor id that no longer exists, which after
      // D10 would be a link to nothing.
      'transfers',
      transfer.id,
    );
  }

  return { ok: true, successorId, successorNumber, stage: data.stage || null };
}

// #567, Session 79.15i — ANSWER EACH LINE, which is the whole issue.
//
// Colby: "each item being transferred has to be able to be accounted for and
// tracked at the individual level... if one item's status is dependent on
// another item that system will always wind up failing."
//
// Austin has the MDO and no frames. Until this, their only honest button was
// Deny, which refused the MDO they were standing next to. Now they answer each
// line and the MDO ships that afternoon.
//
// `answers` is [{ item_id, answer: 'accepted' | 'refused', reason }].
//
// THE HEADER FOLLOWS THE LINES — gate D4, and it is the decision with the
// widest blast radius. Any line accepted means material is moving, so the
// transfer is `approved`. Only an all-refused answer makes it `denied`, which
// keeps `denied` meaning "nothing is coming" for every screen, tab and
// notification that already relies on it.
// ── #646, Session 79.22b — THE REFUSER'S REASONS, QUOTED ─────────────────────
//
// denyTransfer() has always quoted its one reason. The line-by-line path quoted
// none, so the manager asked to "choose what happens next" was given nothing
// to choose by - and "all 100 are committed to the Bexar job" means try another
// branch, while "none left, reorder is in" means wait or buy.
//
// Each reason is the refuser's own words, capped so a long one cannot turn a
// phone's bell list into a wall - the whole of it is on the transfer. Guarded
// the way denyTransfer's item phrase is: a missing name drops the name, and
// nothing ever renders as "undefined" or an empty quote (#514).
export function refusalReasons(answers, items = {}) {
  const cap = (s) => (s.length > 100 ? `${s.slice(0, 99).trimEnd()}…` : s);
  return (Array.isArray(answers) ? answers : [])
    .filter(a => a?.answer === 'refused' && String(a.reason || '').trim())
    .map(a => {
      const why = `"${cap(String(a.reason).trim())}"`;
      const name = items?.[String(a.item_id)]?.name;
      return name ? `${name}: ${why}` : why;
    })
    .join(' · ');
}

// #646, AMENDED Session 79.22b.4.3 -- A FULL STOP AFTER A QUOTE ONLY WHEN THE
// QUOTED WORDS DID NOT END A SENTENCE THEMSELVES. People type reasons as
// sentences -- "None spare." -- and six bells added their own stop after the
// closing quote regardless: `"None spare.". Choose what happens next.` The
// words around the quote are unchanged; only this stop is decided here, from
// the last character inside the last quotation mark. An ellipsis counts as an
// ending: a reason cut at 100 characters already closes on one.
export function stopAfterQuote(quoted) {
  const inner = String(quoted || '').trimEnd().replace(/["”]+$/, '').trimEnd();
  return /[.!?…]$/.test(inner) ? '' : '.';
}

// #619, Session 79.22b -- the running count's object, by gate 567 F2's rule at
// every width: ONE refused item is named, several are "the rest". The phone
// mockup said "the rest" for one and the build followed it; F2 and the desk
// mockup named it, and the gate is amended to agree. With no name in hand it
// says "the rest", which is still true.
export function refusedItemsPhrase(refusedLines, items = {}) {
  const lines = Array.isArray(refusedLines) ? refusedLines : [];
  const name = lines.length === 1 ? items?.[String(lines[0]?.item_id)]?.name : null;
  return name ? `the ${name}` : 'the rest';
}

export async function answerTransferLines(transfer, answers, {
  branchNames = {}, requestedBy = null, items = {},
} = {}) {
  if (!Array.isArray(answers) || answers.length === 0) {
    return { error: new Error('Answer every item before sending.') };
  }
  const missing = answers.find(a => a.answer === 'refused' && !String(a.reason || '').trim());
  if (missing) {
    // Answered HERE as well as by the database so the message can name the item
    // rather than surfacing a constraint name. #499: a CHECK refusal that
    // surfaces nowhere loses what the person typed.
    return { error: new Error('Say why you cannot send every item you are refusing.') };
  }

  const { data, error } = await supabase.rpc('answer_branch_transfer_lines', {
    p_transfer_id: transfer.id,
    p_answers: answers.map(a => ({
      item_id: a.item_id,
      answer: a.answer,
      reason: a.answer === 'refused' ? String(a.reason || '').trim() : null,
    })),
  });
  if (error) return { error };
  // A null with no error is the one thing a confirmed write cannot be — #504,
  // where PostgREST reported zero rows changed as success.
  if (!data) {
    return { error: new Error('That did not save. Nothing has been answered — try again.') };
  }

  const accepted = Number(data.accepted) || 0;
  const refused = Number(data.refused) || 0;
  const numbered = { ...transfer, transfer_number: data.transfer_number || transfer.transfer_number || null };
  const label = transferLabel(numbered, branchNames);
  const refuser = branchNames?.[String(transfer.from_branch_id)] || 'The supplying branch';
  // #646 -- what the refuser said, for every bell below that reports a refusal.
  const reasons = refusalReasons(answers, items);

  // ── SOMETHING IS MOVING ──────────────────────────────────────────────────
  // Mirrors approveTransfer()'s audience exactly rather than inventing one, so
  // the two paths cannot drift apart: the requester plus the sending branch's
  // approvers.
  if (accepted > 0) {
    const senders = await getRequestApproverIds(transfer.from_branch_id, null);
    const audience = [...new Set([requestedBy || transfer.requested_by, ...senders].filter(Boolean))];
    await sendNotification(
      audience,
      refused > 0 ? 'Partly approved — some items could not be sent' : 'Transfer approved',
      refused > 0
        // #705 -- "1 of 2 items are on their way" read wrong.
        ? `${label} — ${accepted} of ${accepted + refused} items ${accepted === 1 ? 'is on its way' : 'are on their way'}${reasons ? `. Not coming: ${reasons}` : ''}`
        // #589 -- the same words as Confirm's bell. Answering item by item
        // takes no date, so a made request says plainly none was promised.
        : `${label} was approved${promisedClause({ mode: transfer.fulfilment_mode, confirmedDate: transfer.confirmed_date || null })}`,
      'transfer_approved',
      'inventory',
      'transfers',
      transfer.id,
    );
  } else {
    // Nothing is coming. The requester is told and does not have to act — their
    // My requests tracker shows it. NO purchase request is named, because under
    // #565's D10 none exists yet: naming one sends somebody looking for a record
    // nobody has raised, which is the shape #559 was logged for.
    // #717 F -- named by what was asked when it never got a number.
    const lead = yourRequestLead(numbered, branchNames, (await readTransferLines(transfer.id)).lines);
    await sendNotification(
      [transfer.requested_by].filter(Boolean),
      'Transfer denied',
      `${lead} — ${refuser} could not send any of it${reasons ? `. ${reasons}` : ''}`,
      'transfer_denied',
      'inventory',
      'transfers',
      transfer.id,
    );
  }

  // ── SOMETHING NEEDS A DECISION ───────────────────────────────────────────
  // D14/#565: the ASKING branch's approving managers, because they are the ones
  // who can act. Sent whenever ANY line was refused — a partly-filled transfer
  // still leaves a decision owed, and that is the case this issue exists for.
  //
  // The refusing branch is NOT told where the lines went — gate D11, Colby's
  // ruling. They have no action, and #500 is the precedent for not paging
  // people who cannot act.
  if (refused > 0) {
    // #650 -- nobody at the asking branch who can decide asks the admins and
    // executives instead.
    const { ids: approvers } = await orEveryoneWhoCanDecide(await getRequestApproverIds(
      transfer.to_branch_id,
      transfer.requested_by_department_id ?? null,
    ), transfer.to_branch_id);
    await sendNotification(
      approvers,
      accepted > 0
        ? `${refuser} couldn't send everything on ${data.transfer_number || 'a transfer'}`
        : `${refuser} refused ${data.transfer_number || 'a transfer'}`,
      `${refuser} can't send ${refused} of ${accepted + refused} item${accepted + refused === 1 ? '' : 's'}.${reasons ? ` ${reasons}${stopAfterQuote(reasons)}` : ''} Choose what happens next.`,
      'transfer_denied_successor',
      'inventory',
      'transfers',
      transfer.id,
    );
  }

  return {
    ok: true,
    status: data.status,
    accepted,
    refused,
    transferNumber: data.transfer_number || null,
  };
}

// #698 half (a), gate 665 D25, Session 79.22b.4.4 -- WHAT AN ITEM-BY-ITEM
// ANSWER DID, in the counts the DATABASE returned rather than the form's (gate
// 665 B3). Lifted out of the Transfers sheet word for word, because the same
// answer is now given from the Requests inbox card too, and one outcome said
// two ways is how the two screens would start to disagree.
export function answeredLinesSentence(result, askerName = null) {
  const asker = askerName || 'The branch that asked';
  const accepted = Number(result?.accepted) || 0;
  const refused = Number(result?.refused) || 0;
  if (accepted > 0) {
    return refused > 0
      ? `${accepted} of ${accepted + refused} on their way. ${asker} will decide what happens to the rest.`
      : 'Approved. The branch that asked has been told.';
  }
  return `Refused. ${asker} will decide what happens next.`;
}

// #565 D5/D6/D8 + #567 — WHAT HAPPENS TO THE LINES THAT WERE REFUSED.
//
// The refusal creates no document. It comes home to the ASKING branch's
// approving manager, who chooses: ask the maker, try another branch, send it to
// Purchasing, or decide nothing more is needed.
//
// `route` is 'make' | 'branch' | 'buy' | 'none'. `itemIds` names which refused
// lines this decision covers; null means all of them, which is the screen's
// normal call.
//
// THE QUANTITY IS A FRESH DECISION AND NOT AN INHERITANCE — gate D9, Colby's
// ruling, and he overturned a narrower recommendation of mine to get here:
// "houston makes pallets of stakes at a quantity of 250. so the items that are
// coming back to dallas after austin denyed the transfer request need to have
// the option for the quantities to be adjusted too." A production run has batch
// economics exactly as a purchase order has vendor minimums, so the quantity is
// NOT capped at what was originally asked.
//
// THE PICKER IS ADVISORY AND THE DATABASE IS THE ENFORCEMENT. Every refusal
// below — a route that cannot work, the branch that just refused, a second
// manager who got there first — is raised by the function, not merely greyed on
// the screen, and is proven by calling it directly rather than through the UI.
// ── #621, Session 79.22b — WHAT A REFUSAL WAS ROUTED TO ─────────────────────
//
// The document a routing decision raised, if any: a transfer asked of another
// branch or the maker, or a purchase request for Purchasing. Two plain reads,
// the same two the Transfers page uses to know a refusal is decided, because
// a filter through a nested relation returns empty silently. Null when the
// decision was "nothing more is needed", which raises nothing.
export async function fetchRefusalSuccessor(transferId) {
  if (transferId == null) return null;
  const [{ data: t }, { data: m }] = await Promise.all([
    supabase.from('branch_transfers').select('id, transfer_number').eq('from_transfer_id', transferId).limit(1),
    supabase.from('material_requests').select('id, request_number').eq('from_transfer_id', transferId).limit(1),
  ]);
  if (t && t[0]) return { kind: 'transfer', id: t[0].id, number: t[0].transfer_number || null };
  if (m && m[0]) return { kind: 'request', id: m[0].id, number: m[0].request_number || null };
  return null;
}

// #620, Session 79.22b -- THE SENTENCE AFTER A REFUSAL IS ROUTED NAMES WHO WAS
// TOLD. Gate 565 F2's template, "{number} raised — {Destination} has been
// told", is right wherever the destination and the people told are the same
// party, and for the shelf and make routes they are. For BUY they are not: the
// new material request waits on the ASKING branch's approval before
// Purchasing sees it, so "Purchasing has been told" was false -- Purchasing
// had been told nothing. And when nobody at a branch could decide, #650 asks
// every admin and executive, which is said rather than hidden.
export function routedSentence(route, result, { askingBranch = null, destination = null } = {}) {
  const num = result?.successorNumber || null;
  const told = result?.told || null;
  if (route === 'buy') {
    const branch = askingBranch || 'your branch';
    const head = num ? `${num} raised` : 'The purchase request is raised';
    if (told && told.count === 0) {
      return `${head}. It needs ${branch}'s approval before Purchasing sees it, and nobody is set up to give it.`;
    }
    if (told?.fallback) {
      return `${head}. Nobody at ${branch} can approve it, so every admin and executive has been told. Purchasing sees it once it is approved.`;
    }
    return `${head} — ${branch}'s approvers have been told. Purchasing sees it once it is approved.`;
  }
  const dest = destination || (route === 'make' ? 'the maker' : 'that branch');
  const head = num ? `${num} raised` : `Asked ${dest}`;
  if (told && told.count === 0) return `${head}, but nobody is set up to confirm it.`;
  if (told?.fallback) return `${head}. Nobody at ${dest} can confirm it, so every admin and executive has been told.`;
  return num ? `${num} raised — ${dest} has been told.` : `Asked ${dest} — they have been told.`;
}

export async function routeDeniedTransfer(transfer, {
  route, qty = null, toBranchId = null, itemIds = null, branchNames = {},
} = {}) {
  if (!route) return { error: new Error('Choose what happens next.') };

  const { data, error } = await supabase.rpc('route_denied_transfer', {
    p_transfer_id: transfer.id,
    p_route: route,
    p_qty: qty == null || qty === '' ? null : Number(qty),
    p_to_branch_id: toBranchId ?? null,
    p_item_ids: itemIds && itemIds.length ? itemIds : null,
  });
  if (error) {
    // #621 -- LOSING THE RACE SAYS WHAT WON. The database refuses a second
    // decision, rightly; the sentence it refuses with said somebody decided and
    // not what, so a manager who had just confirmed "this closes the transfer"
    // was left to go and look. Gate 565 F2: "Someone has already decided this
    // one. See {number}". A decision that raised nothing keeps the plain
    // sentence - there is nothing to see.
    if (/^Someone has already decided this one\.$/.test(String(error.message || '').trim())) {
      const won = await fetchRefusalSuccessor(transfer.id);
      if (won?.number) return { error: new Error(`Someone has already decided this one. See ${won.number}.`) };
    }
    return { error };
  }
  if (!data) {
    return { error: new Error('That did not save. Nothing has been routed — try again.') };
  }

  // "Nothing more is needed" writes no document and tells nobody. The only
  // person who might care is the original requester, and they see it on My
  // requests — a bell saying "your manager decided you don't need this" is a
  // notification nobody can act on.
  //
  // #578, Session 79.21b -- `closed` says what actually happened. A wholly
  // refused transfer is closed; a partly refused one carries on for what was
  // agreed, and the decision is recorded on it instead. The screen used to say
  // "Closed with nothing ordered" in both cases while the second wrote nothing.
  if (route === 'none') {
    return { ok: true, route, closed: data.closed === true, successorId: null, successorNumber: null };
  }

  const successorId = data.successor_id || null;
  const successorNumber = data.successor_number || null;
  // #717 B and C -- the two bells below name what is now asked for, read back
  // from the document the database just raised (the quantity is a fresh
  // decision, gate 565 D9, so the refused transfer's own numbers may not be
  // it), and who refused it -- never "Not yet numbered" inside a sentence.
  const refuser = branchNames?.[String(transfer.from_branch_id)] || 'The other branch';
  const askingName = branchNames?.[String(transfer.to_branch_id)] || 'A branch';
  // "Part of" only when something on the refused transfer was agreed -- the
  // same test the database uses to decide whether "nothing more" closes it.
  const partly = (transfer.branch_transfer_items || []).some(l => l.line_status === 'accepted');
  // #620 -- WHO WAS TOLD, so the screen can name them rather than the
  // destination: for the buy route those are different parties.
  let told = null;

  if (data.successor_kind === 'material_request') {
    // Whoever an ordinary material request notifies — `restock_request`, domain
    // `inventory`, opening the Requests inbox. NOT `request_submitted`, which
    // #565's gate claimed existed and which exists on neither database: it is
    // one of the values Session 79.9 invented, and sending it would have the
    // database refuse the message telling Purchasing anything at all.
    const asked = await orEveryoneWhoCanDecide(await getRequestApproverIds(
      transfer.to_branch_id,
      transfer.requested_by_department_id ?? null,
    ), transfer.to_branch_id);
    told = { branchId: transfer.to_branch_id, fallback: asked.fallback, count: asked.ids.length };
    const what = itemsPhrase(await readRequestLines(successorId)) || 'material';
    const part = partly ? `, part of ${transfer.transfer_number || 'the transfer'}` : '';
    await sendNotification(
      asked.ids,
      'Buy it instead — needs your approval',
      `${refuser} refused ${what}${part}, so ${successorNumber || 'a material request'} is waiting for your approval.`,
      'restock_request',
      'inventory',
      'requests_inbox',
      successorId,
    );
  } else if (data.successor_kind === 'branch_transfer') {
    // The NEW supplying branch's approvers, plus the fabrication department
    // manager on a make route — the same calls createTransfer() makes, so the
    // two audiences cannot drift apart.
    const dest = toBranchId ?? transfer.made_at_branch_id;
    const approvers = await getRequestApproverIds(dest, null);
    const fabricators = route === 'make'
      ? await fabricationManagerIds(dest, transfer.manufacturing_department_id ?? null)
      : [];
    const asked = await orEveryoneWhoCanDecide([...approvers, ...fabricators], dest);
    told = { branchId: dest, fallback: asked.fallback, count: asked.ids.length };
    const read = await readTransferLines(successorId);
    const what = itemsPhrase(read.lines) || 'material';
    const destName = branchNames?.[String(dest)] || 'your branch';
    await sendNotification(
      asked.ids,
      route === 'make' ? 'Material to make for another branch' : 'Transfer requested',
      `${askingName} is asking ${destName} ${route === 'make' ? 'to make' : 'for'} ${what} after ${refuser} refused it — waiting on you to confirm`,
      'transfer_requested',
      'inventory',
      'transfers',
      successorId,
    );
    // #718, Session 79.22b.4.8 -- the new request is raised APPROVED by the
    // person who routed it, at the new supplier's stage, so that branch is told
    // the way every request reaching it is: the bell above AND the approval
    // email a first-stage approval sends. The request is described as the
    // database raised it - from the new supplier to the asking branch.
    await emailSupplyingBranch({
      id: successorId,
      transfer_number: successorNumber,
      from_branch_id: dest,
      to_branch_id: transfer.to_branch_id,
      fulfilment_mode: route === 'make' ? 'make' : 'stock',
      needed_by: transfer.needed_by ?? null,
    }, { branchNames, read });
  }

  return { ok: true, route, successorKind: data.successor_kind, successorId, successorNumber, lines: data.lines, told };
}

// "WE MADE THESE" — #553 D5, Session 79.15e. The event that moves `qty_ready`
// and lets a truck be loaded.
//
// ONE RPC, because three things move together: the production row that records
// who and when, the running total on the line, and the frames landing on the
// maker's own rack. Locked rule 7. Two client-side writes would leave a
// signed-off run with no quantity behind it — #529's stranding in a new
// costume, and a run nobody can load is worse than one nobody recorded.
//
// IT RETURNS THE NUMBER THE DATABASE ENDED UP WITH, and the caller renders THAT
// rather than the number the person typed. `error === null` is not proof a
// write happened: PostgREST reports zero rows changed as success, which is how
// 79.1's packing-slip control said "Added just now" and wrote nothing. A null
// coming back with no error is therefore treated as a FAILURE here, because the
// one thing it cannot be is a confirmed write.
//
// #665 D7, Session 79.22b.4.5 -- EVERY ITEM ON A CARD, IN ONE PRESS, ALL OR
// NOTHING. The To make card signs off several items at once and the Sign off
// sheet signs off one, and both come through signOffProductionLines(), so the
// call, the bell and the own-shelf finish live in one place (locked rule 14).
// Its database step, sign_off_production_lines(), calls sign_off_production()
// once per item inside one transaction: a refusal on any item takes every item
// back, and production still has exactly one writer.
//
// `entries` is [{ line, qty }]. A quantity of 0 is LEFT OUT, never sent -- a
// box typed 0 on the card means "none of this one", Colby's partial case -- and
// a press with nothing left in it is refused here, before the database.

// "25 Stake H-Wire", "25 Stake H-Wire and 10 Frame WA 24x18", "a, b and c".
export function andList(parts) {
  const xs = (parts || []).filter(p => p != null && String(p).trim() !== '').map(String);
  if (xs.length <= 1) return xs[0] || '';
  return `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}

// What a press made, by name: "50 Stake H-Wire". The number alone when no name
// is to hand, which is what the bell said for every press before #665.
export function madeList(signed) {
  return andList((signed || []).map(s => (s.name ? `${s.qty} ${s.name}` : `${s.qty}`)));
}

export async function signOffProductionLines(transfer, entries, { branchNames = {}, items = {}, notes = null } = {}) {
  const sending = (Array.isArray(entries) ? entries : [])
    .map(e => ({ line: e?.line, qty: Number(e?.qty) }))
    .filter(e => e.line?.id != null && Number.isFinite(e.qty) && e.qty > 0);
  if (sending.length === 0) return { error: new Error('Say how many you made.') };
  if (transfer?.id == null) return { error: new Error('No transfer to sign off against.') };

  const { data, error } = await supabase.rpc('sign_off_production_lines', {
    p_transfer_id: transfer.id,
    p_lines: sending.map(e => ({ line_id: e.line.id, qty: e.qty })),
    p_notes: notes,
  });
  if (error) return { error };
  // An answer that does not account for EVERY item sent is not a confirmed
  // write, for the reason above: `error === null` alone proves nothing.
  const back = Array.isArray(data?.lines) ? data.lines : null;
  const readyByLine = new Map((back || []).map(l => [String(l?.line_id), l?.ready_now == null ? null : Number(l.ready_now)]));
  if (!back || sending.some(e => readyByLine.get(String(e.line.id)) == null)) {
    return { error: new Error('That did not save. Nothing has been signed off — try again.') };
  }
  // #694 -- the run each item's press wrote, which is what its Undo cites.
  // Null where the database did not name one (a body older than
  // 20260927_undo_sign_off_694), and the card then offers no Undo at all.
  const runByLine = new Map(back.map(l => [String(l?.line_id), l?.production_id ?? null]));
  const signed = sending.map(e => ({
    lineId: e.line.id,
    itemId: e.line.item_id,
    qty: e.qty,
    requested: Number(e.line.qty_requested) || 0,
    readyNow: readyByLine.get(String(e.line.id)),
    productionId: runByLine.get(String(e.line.id)) ?? null,
    name: items?.[String(e.line.item_id)]?.name || e.line?.inventory_items?.name || null,
  }));

  // THE ASKING BRANCH'S MANAGER, and nobody else. They are the person whose
  // week changes when material they are waiting on becomes real; the maker's
  // own branch watched somebody press the button.
  //
  // NOT the fulfiller — #514 settled that at 79.7 and this inherits it rather
  // than reopening it: 83% of non-desk workers have no regular email access, so
  // a message to the person cutting steel is unreliable by construction. This
  // is an in-app bell only.
  //
  // #580 part a, gate 553 D26 -- NOT for a request the branch made for its own
  // shelf: this bell's audience is the asking branch's managers, and there they
  // are the branch doing the work. Its asker hears once, when the last run is
  // signed off and the request finishes (the database finishes it).
  //
  // #649, Session 79.22b -- "the asking branch's manager" now includes the
  // manager of the asker's department, who approved stage one: this passed a
  // literal null, which reaches the branch manager alone.
  //
  // #665 D7 -- ONE MESSAGE PER PRESS, NAMING EVERY ITEM SIGNED OFF, and only
  // those: Colby's 50-and-0 press says "50 Stake H-Wire ready on ...", never
  // the frames it left at 0. It said "50 ready on ..." (gate 665 Screen 1b).
  if (transfer?.to_branch_id && !isOwnShelf(transfer)) {
    const askers = await askingApproverIds(transfer);
    const audience = [...new Set((askers || []).filter(Boolean))];
    if (audience.length) {
      await sendNotification(
        audience,
        'Material made and ready',
        `${madeList(signed)} ready on ${transferLabel(transfer, branchNames)}`,
        'transfer_production_signed_off',
        'inventory',
        'transfers',
        transfer.id,
      );
    }
  }

  let finished = false;
  if (isOwnShelf(transfer)) {
    const { data: now } = await supabase
      .from('branch_transfers').select('status').eq('id', transfer.id).maybeSingle();
    finished = now?.status === 'received';
    if (finished && transfer.requested_by) {
      const total = makeProgress(transfer.branch_transfer_items).requested || null;
      const branch = branchNames?.[String(transfer.from_branch_id)] || 'the';
      await sendNotification(
        [transfer.requested_by],
        'Made for your shelf',
        // #705 -- "All 1 are on Houston's shelf" read wrong for a request of one.
        `${Number(total ?? signed[0].readyNow) === 1 ? 'The 1 is' : `All ${total ?? signed[0].readyNow} are`} on ${branch}${branch === 'the' ? '' : "'s"} shelf — ${transferLabel(transfer, branchNames)}`,
        'transfer_received',
        'inventory',
        'transfers',
        transfer.id,
      );
    }
  }

  return { lines: signed, finished };
}

// #694, gate 665 D21, Session 79.22b.4.6 -- TAKING A SIGN-OFF BACK.
//
// Every run ONE press wrote, undone in one database call, all or none:
// undo_sign_off_production() puts the maker's shelf and the made count back,
// writes a reversal beside each run and each stock row rather than deleting
// either (SAP's reversal document), and reopens a request for the branch's own
// shelf that the press had finished. It refuses anybody but the person who
// pressed, a press already undone, and one with anything on a truck or a
// transfer closed since - in sentences written for a person. No time limit:
// Colby's ruling at this session's open (R2).
//
// SENDS NOTHING (gate 665 B5): a reversed press was a mistake, and a second
// message about it is noise. The message the press itself sent stays, and the
// transfer's history shows the press and its Undo (R2).
//
// `signed` is signOffProductionLines()'s own `lines`. As with the sign-off, an
// answer that does not account for every run is not a confirmed write.
export async function undoSignOff(signed) {
  const runs = (Array.isArray(signed) ? signed : []).filter(Boolean);
  const ids = runs.map(s => s.productionId);
  if (runs.length === 0 || ids.some(id => id == null)) {
    return { error: new Error("That can't be undone from here. Tell a manager so the count can be corrected.") };
  }
  const { data, error } = await supabase.rpc('undo_sign_off_production', { p_production_ids: ids });
  if (error) return { error };
  const back = Array.isArray(data?.lines) ? data.lines : null;
  const byRun = new Map((back || []).map(l => [String(l?.production_id), l]));
  if (!back || ids.some(id => !byRun.has(String(id)))) {
    return { error: new Error('That did not go through. Nothing was undone — try again.') };
  }
  return {
    lines: runs.map(s => ({
      lineId: s.lineId,
      qty: s.qty,
      readyNow: Number(byRun.get(String(s.productionId)).ready_now),
    })),
    reopened: !!data.reopened,
  };
}

// ONE ITEM, from the Sign off sheet -- the same call, the same bell.
export async function signOffProduction(line, qty, { transfer, branchNames = {}, items = {}, notes = null } = {}) {
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return { error: new Error('Say how many you made.') };
  if (!line?.id) return { error: new Error('No line to sign off against.') };
  const r = await signOffProductionLines(transfer || { id: line.transfer_id }, [{ line, qty: n }],
    { branchNames, items, notes });
  if (r.error) return { error: r.error };
  return { readyNow: r.lines[0].readyNow, finished: r.finished };
}

// #580 part a, gate 553 D21-D23 and D26, Session 79.21b -- "MAKE THESE" FOR
// OUR OWN SHELF.
//
// One database call writes the request and its line (locked rule 7), stamps
// the shelf count as the note's first line (D22), and - when the person may
// confirm and passes a date - confirms it in the same call (D23). The database
// refuses anybody whose branch does not make the item; who may confirm is the
// app's rule (canConfirmSupply), as it is for every confirmation.
//
// WHO IS TOLD (D26): the people who may confirm - the branch's approvers and,
// the fabrication manager - by bell and the approver email, but only when it is
// waiting on them. Raised already confirmed, nobody is asked to confirm it.
export async function requestOwnShelfMake({
  itemId, qty, neededBy = null, note = null, confirmedDate = null,
  branchId, manufacturingDepartmentId = null, branchNames = {},
  requesterName = null, itemName = null, uom = null,
} = {}) {
  const n = Number(qty);
  if (!Number.isFinite(n) || n <= 0) return { error: new Error('Say how many to make.') };
  if (itemId == null) return { error: new Error('Choose what to make.') };

  const { data, error } = await supabase.rpc('request_own_shelf_make', {
    p_item_id: itemId,
    p_qty: n,
    p_needed_by: neededBy || null,
    p_note: note && String(note).trim() ? String(note).trim() : null,
    p_confirmed_date: confirmedDate || null,
  });
  if (error) return { error };
  if (!data || data.id == null) {
    return { error: new Error('That did not save. Nothing was asked for — try again.') };
  }

  const transfer = {
    id: data.id, transfer_number: data.transfer_number || null, status: data.status,
    from_branch_id: branchId, to_branch_id: branchId, fulfilment_mode: 'make',
    manufacturing_department_id: manufacturingDepartmentId,
  };

  if (data.status !== 'approved') {
    const label = transferLabel(transfer, branchNames);
    const approvers = await getRequestApproverIds(branchId, null);
    const fabricators = await fabricationManagerIds(branchId, manufacturingDepartmentId);
    // #650 -- nobody here who can confirm it asks the admins and executives.
    const { ids: confirmers } = await orEveryoneWhoCanDecide([...approvers, ...fabricators], branchId);
    await sendNotification(
      confirmers,
      'Asked to make for our own shelf',
      // Not "on To make": it reaches To make only once somebody confirms it.
      // Until then it waits in the Requests inbox and on Awaiting
      // confirmation, which is where this bell's link opens it.
      // #717 H, the approved drawing -- the label sat in the middle of this
      // sentence as "Not yet numbered · For Houston's own shelf"; the shelf
      // is now said in words.
      `${requesterName || 'Someone'} asked for ${n}${itemName ? ` ${itemName}` : ''} for `
        + `${branchNames?.[String(branchId)] || 'this branch'}'s own shelf — waiting on you to confirm a date`,
      'transfer_requested',
      'inventory',
      'transfers',
      data.id,
    );
    await notifyTransferRequested({
      stage: 'supply',
      recipientBranchId: branchId,
      recipientDepartmentId: null,
      label,
      fromBranchName: branchNames?.[String(branchId)] || 'a branch',
      toBranchName: branchNames?.[String(branchId)] || 'a branch',
      requesterName,
      mode: 'make',
      neededBy,
      lines: [{ name: itemName, qty: n, uom }],
      transferId: data.id,
    });
  }

  return { transfer, status: data.status, transferNumber: data.transfer_number || null };
}

// #590, gate 371 D31-D37, Session 79.21b -- TAKING A REQUEST BACK.
//
// `scope` is 'whole' (nothing loaded yet: it ends Withdrawn) or 'rest' (part
// is on the road: that part still arrives and is counted in; the rest is never
// sent). It is passed rather than inferred so the database refuses - and the
// screen can explain - a truck that was loaded a second before the press,
// instead of silently doing the other thing (#492's class).
//
// WHO IS TOLD (D36), by where it had got to: waiting on the asker's own
// approvers -> those approvers; waiting on or confirmed by the supplying branch
// -> that branch's approvers, plus the fabrication manager on a make request.
// Always the person who asked, when somebody else withdrew it. Never the person
// who pressed it. transfer_closed_unfilled is NOT sent for 'rest': its audience
// is the asking branch, which is who pressed the button.
export async function withdrawTransfer(transfer, {
  scope, reason, withdrawnBy = null, withdrawnByName = null, branchNames = {},
} = {}) {
  const why = String(reason || '').trim();
  if (!why) return { error: new Error('Say why you are withdrawing it.') };
  if (scope !== 'whole' && scope !== 'rest') {
    return { error: new Error('Choose whether to withdraw the whole request or stop the rest.') };
  }

  const { data: ending, error } = await supabase.rpc('withdraw_branch_transfer', {
    p_transfer_id: transfer.id,
    p_scope: scope,
    p_reason: why,
  });
  if (error) return { error };
  if (ending !== 'cancelled' && ending !== 'closed_unfilled') {
    return { error: new Error('That did not save. Nothing was withdrawn — try again.') };
  }

  const waitingOnAsker = transfer.status === 'requested';
  const told = waitingOnAsker
    ? await getRequestApproverIds(transfer.to_branch_id, transfer.requested_by_department_id ?? null)
    : [
      ...(await getRequestApproverIds(transfer.from_branch_id, null)),
      ...(transfer.fulfilment_mode === 'make'
        ? await fabricationManagerIds(transfer.from_branch_id, transfer.manufacturing_department_id ?? null)
        : []),
    ];
  if (transfer.requested_by && withdrawnBy && String(transfer.requested_by) !== String(withdrawnBy)) {
    told.push(transfer.requested_by);
  }
  const audience = [...new Set(told.filter(Boolean).map(String))]
    .filter(id => !withdrawnBy || id !== String(withdrawnBy));
  // #639 -- THE ASKER GETS THEIR OWN SENTENCE when somebody else withdrew it.
  // The branch-voiced one below is written for the other branch and is right
  // there; sent to the person whose request it was, it said her own branch
  // did something to her and never who. One body goes to one list of
  // recipients, so two audiences told two things means two calls - and the
  // asker comes OUT of the first, so nobody gets both.
  const askerId = transfer.requested_by && withdrawnBy
    && String(transfer.requested_by) !== String(withdrawnBy)
    ? String(transfer.requested_by) : null;
  const branchAudience = audience.filter(id => id !== askerId);

  const asker = branchNames?.[String(transfer.to_branch_id)] || 'The asking branch';
  // A request withdrawn before it was numbered has no label that reads inside a
  // sentence -- "Dallas withdrew Not yet numbered · Houston → Dallas" -- so it is
  // named by what it was. The gate drew only a numbered one (F2's example);
  // found proving W8 at 79.21b. Stopping the rest is never unnumbered, since
  // something has been sent.
  const label = transfer.transfer_number ? transferLabel(transfer, branchNames)
    : isOwnShelf(transfer) ? 'its request to make these for its own shelf'
      : `its request to ${branchNames?.[String(transfer.from_branch_id)] || 'the other branch'}`;
  // D35 -- anything already made stays on the maker's shelf, and the bell says
  // so. Only on a make request: a shelf transfer's "ready" was never made.
  const made = transfer.fulfilment_mode === 'make'
    ? makeProgress(transfer.branch_transfer_items).readyToLoad
    : 0;
  // #705 -- the verb follows the number: "The 1 already made stays".
  const madeLine = made > 0 ? ` The ${made} already made ${made === 1 ? 'stays' : 'stay'} on your shelf.` : '';
  // #646 amended -- no second full stop after a reason typed as a sentence.
  const stop = stopAfterQuote(why);
  const body = ending === 'cancelled'
    ? `${asker} withdrew ${label}: “${why}”${stop}${madeLine}`
    : `${asker} doesn't need the rest of ${label}: “${why}”${stop} What is already on the road still arrives.${madeLine}`;

  if (branchAudience.length) {
    await sendNotification(branchAudience, 'Request withdrawn', body, 'transfer_withdrawn', 'inventory', 'transfers', transfer.id);
  }
  if (askerId) {
    const who = withdrawnByName || 'Somebody else';
    const num = transfer.transfer_number ? ` (${transfer.transfer_number})` : '';
    const what = isOwnShelf(transfer)
      ? `your request to make these for your own shelf${num}`
      : `your request to ${branchNames?.[String(transfer.from_branch_id)] || 'the other branch'}${num}`;
    const mine = ending === 'cancelled'
      ? `${who} withdrew ${what}: “${why}”${stop}`
      : `${who} stopped the rest of ${what}: “${why}”${stop} What is already on the road still arrives.`;
    await sendNotification([askerId], 'Request withdrawn', mine, 'transfer_withdrawn', 'inventory', 'transfers', transfer.id);
  }
  return { ok: true, ending };
}

// #585, Session 79.21b -- THE DATABASE'S OWN WORDS, TURNED INTO A SENTENCE.
//
// The older transfer functions -- approve, endorse, load, count in, sign off,
// ship, receive, close -- refuse in their own internal shorthand: "approve_
// branch_transfer: transfer 12 is approved, not awaiting confirmation". The
// screens printed that as it came. Newer functions already speak plainly and
// pass straight through. Done here, in the app, so no migration joins the ones
// waiting for production.
//
// `race` is true when the refusal means somebody else got there first, or the
// transfer has moved on: the caller reloads so the person sees where it stands.
// Anything with the internal "name: ..." shape that is not recognised still
// becomes a sentence (gate 567 F1) -- never the raw text.
const RAW_REFUSAL = /^([a-z_]+): /;
const ENDED_SENTENCE = {
  cancelled: 'This request was withdrawn, so there is nothing more to do on it.',
  closed_unfilled: 'Nothing more is being sent on this transfer.',
  closed_short: 'This transfer was closed short.',
  received: 'Everything on this transfer has already arrived.',
  denied: 'This transfer was refused.',
};
// Sentences the newer functions already write for a person that ALSO mean
// somebody got there first -- a routing race (#585's last case, gate 565 F1)
// read the right words and left the panel where it was.
const PLAIN_RACES = [
  // #621 -- with the document the winning decision raised, when there is one.
  /^Someone has already decided this one\.( See [A-Z0-9-]+\.)?$/,
  /^This transfer is no longer waiting for a decision\.$/,
  /^That transfer no longer exists\.$/,
  /^This request was withdrawn, so there is nothing left to decide\.$/,
  / already withdrew this\.$/,
  // #694 -- an Undo refused because the transfer moved on since the press (a
  // truck loaded, the transfer closed) or because the same press was already
  // taken back: the card is out of date either way, so the page reads again.
  /^That can't be undone now — /,
  /^That sign-off has already been undone\.$/,
  // #694, 79.22b.4.6.1 -- the same for a send, and "It is here" pressed on a
  // truck its sender took back from a screen that had not caught up.
  /^That send has already been undone\.$/,
  /^That truck was taken back by the person who sent it/,
  // #694, 79.22b.4.6.1b -- the same for a collection.
  /^That collection has already been undone\.$/,
  // #694, 79.22b.4.6.2 -- and for a count in.
  /^That count has already been undone\.$/,
  // #682 / #702, 79.22b.6c.2 -- an Undo refused because a correction has
  // touched the record since ("That sign-off was corrected since, so it can't be
  // undone."), and a correction that lost to another one made a moment earlier.
  // Either way somebody else got there first, so the page reads again.
  / was corrected since, so it can't be undone/,
  /^Someone corrected this a moment ago, so nothing was changed\./,
  // #682, 79.22b.6c.2b -- Houston's answer to a truck's extra, refused because
  // somebody else answered first ("Nina Hart already answered: 4 went."), the
  // counting branch took its count back, or the question has gone. The card is
  // out of date each time, so the page reads again (answer_load_extra()).
  / already answered: /,
  / took that count back, so there is nothing to answer\.$/,
  /^That question no longer exists, so nothing was answered\.$/,
];
export function plainTransferRefusal(message) {
  const raw = String(message || '').trim();
  if (!raw) return { text: "That didn't go through. Try again.", race: false };
  // #587 -- the one refusal that arrives as a constraint name rather than a
  // function's words: booking more than was sent at the destination (371 D10).
  if (/branch_transfer_items_received_within_shipped/.test(raw)) {
    return { text: "More than was sent can't be booked in here. Change the count to no more than was sent.", race: false };
  }
  const fn = (raw.match(RAW_REFUSAL) || [])[1];
  if (!fn) return { text: raw, race: PLAIN_RACES.some(re => re.test(raw)) };   // already plain
  const rest = raw.slice(fn.length + 2);
  let m;
  if (/ does not exist$/.test(rest)) return { text: 'That transfer no longer exists.', race: true };
  if (/^(approve|endorse|deny)_branch_transfer$/.test(fn) && /^transfer \d+ is \w+, not /.test(rest)) {
    return { text: 'Someone has already decided this one.', race: true };
  }
  if ((m = rest.match(/^transfer \d+ is (\w+), not /))) {
    return { text: ENDED_SENTENCE[m[1]] || 'Someone has already changed this transfer.', race: true };
  }
  if ((m = rest.match(/^([\d.]+) of item \d+ asked for but only ([\d.]+) standing at branch \d+/))) {
    // #705 -- "Only 1 are there" read wrong.
    return { text: `Only ${Number(m[2])} ${Number(m[2]) === 1 ? 'is' : 'are'} there to load now — someone may have just loaded some.`, race: true };
  }
  if (/^load \d+ was already counted in/.test(rest)) return { text: 'This truck has already been counted in.', race: true };
  // #694, 79.22b.4.6.1 -- a truck its sender took back never set off.
  if (/^load \d+ is undone, which cannot be counted in/.test(rest)) {
    return { text: 'That truck was taken back by the person who sent it, so there is nothing to count in.', race: true };
  }
  if (/^load \d+ is \w+, which cannot be counted in/.test(rest)) {
    return { text: "This truck can't be counted in from where it is now.", race: true };
  }
  if ((m = rest.match(/^only ([\d.]+) were asked for and ([\d.]+) are already signed off/))) {
    return {
      // #705 -- the two verbs follow their two numbers.
      text: `Only ${Number(m[1])} ${Number(m[1]) === 1 ? 'was' : 'were'} asked for, and ${Number(m[2])} ${Number(m[2]) === 1 ? 'is' : 'are'} already signed off — someone may have just signed off a run.`,
      race: true,
    };
  }
  if (/a reason is required$/.test(rest)) return { text: 'Say why.', race: false };
  if (/a count cannot be negative/.test(rest)) return { text: "A count can't be below zero.", race: false };
  if (/how many did you make\? must be more than zero$/.test(rest)) return { text: 'Enter how many were made.', race: false };
  // Sentences the database already writes for a person, minus the prefix.
  if (/^(production is signed off by the branch making it|this item was refused, so nothing is made for it)$/.test(rest)) {
    return { text: rest.charAt(0).toUpperCase() + rest.slice(1) + '.', race: false };
  }
  return { text: "That didn't go through — someone may have changed this transfer. Here is where it stands now.", race: true };
}

// #590, gate 371 D31-D37 and F2 -- WHAT THE WITHDRAW CONTROL OFFERS ON A
// TRANSFER, AND IN WHAT WORDS. One copy for both screens that offer it: My
// requests (the asker) and the transfer's detail (the asker's managers). Null
// when nothing is offered -- the supplying branch, an ended request, or
// everything already sent (F1: an absent control is honest where a dead one
// is not). `approverName` names the asker's own approver while the request
// still waits on them (D36); without it the sentence names their branch.
export function transferWithdrawOffer(transfer, { profile = null, approverName = null, branchNames = {}, askerName = null } = {}) {
  if (!transfer || !profile || !canWithdrawTransfer(profile, transfer)) return null;
  // W8 and B5 -- THE PERSON WHO ASKED IS TOLD WHEN SOMEBODY ELSE WITHDRAWS IT,
  // so the sheet says so before the press, the way gate 391's cancel names
  // both people it will tell. Built wider than 371 F2 drew it, at 79.21b: the
  // approved string named only the supplying branch, and a manager withdrawing
  // for a team member would not have known the team member hears about it.
  const forSomeoneElse = transfer.requested_by != null && String(transfer.requested_by) !== String(profile.id);
  const alsoTold = !forSomeoneElse ? ''
    : askerName ? ` ${askerName}, who asked for it, will be told too.`
      : ' The person who asked for it will be told too.';
  const lines = agreedLines(transfer.branch_transfer_items || []);
  const sum = k => lines.reduce((n, l) => n + (Number(l[k]) || 0), 0);
  const requested = sum('qty_requested');
  const sent = sum('qty_shipped');
  // #640, Session 79.22b.1 -- THE SAME SUM THE BELL USES, AND THE DIFFERENCE
  // ONLY BECAME VISIBLE WHEN THE SHEET STARTED PRINTING THE NUMBER.
  //
  // This was `max(SUM(ready) - SUM(shipped), 0)`: totals first, clamp after.
  // `withdrawTransfer()`'s bell uses makeProgress().readyToLoad, which clamps
  // PER LINE and then adds. They agree on a transfer where nothing has shipped
  // -- which is every case the old code printed a number for -- and they part
  // company the moment one line ships more than it made. That is reachable
  // rather than theoretical: a line on a make transfer can be filled off the
  // supplying branch's own shelf (`sourced`), so ready stays 0 while shipped
  // does not, and one line's surplus would silently cancel another line's
  // leftovers. The asker's sheet would have said 5 while Houston's bell said 10
  // about the same frames.
  //
  // PER LINE IS THE RIGHT ONE: material left standing on a rack cannot be
  // negative, so nothing another line did can reduce it.
  const made = transfer.fulfilment_mode === 'make'
    ? makeProgress(transfer.branch_transfer_items || []).readyToLoad
    : 0;
  const supplier = branchNames[String(transfer.from_branch_id)] || 'the other branch';
  const asking = branchNames[String(transfer.to_branch_id)] || 'your branch';
  const subtitle = transferLabel(transfer, branchNames);
  if (sent <= 0) {
    // D32 -- nothing loaded: the whole request, ending Withdrawn.
    const who = transfer.status === 'requested'
      ? (approverName || `${asking}'s approvers`)
      : supplier;
    return {
      scope: 'whole', control: 'Withdraw this request', title: 'Withdraw this request?', subtitle,
      consequence: `${who} will be told and it comes off their list.`
        + (made > 0 ? ` The ${made} already made ${made === 1 ? 'stays' : 'stay'} on ${supplier}'s shelf.` : '')
        + ' Nothing has been sent.' + alsoTold,
      who, alsoToldName: forSomeoneElse ? (askerName || null) : null,
      // #643 -- `who` is "Dallas's approvers" when no single name was found,
      // which read "so Dallas's approvers knows".
      reasonLabel: `Why? ${who} will see this.`, blockedReason: `Say why, so ${who} ${knowsVerb(who)}.`,
      keepLabel: 'Keep it', submitLabel: 'Withdraw request',
    };
  }
  const left = Math.max(requested - sent, 0);
  if (left <= 0) return null;
  // D33 -- part is on the road: that part still arrives; the rest never leaves.
  return {
    scope: 'rest', control: "We don't need the rest", title: 'Stop the rest of this request?', subtitle,
    // #640 -- WHAT HAPPENS TO WHAT IS ALREADY BUILT, which this branch never
    // said. Dallas stops the rest of 85 stakes; 20 are on the road and 15 more
    // are standing finished on Houston's rack. The sheet named the 20 and the
    // 65, and never mentioned the 15 -- so the person pressing an irreversible
    // button could not tell whether they were scrapped, unmade or simply
    // Houston's stock now. The sibling branch above has said it all along, and
    // so does the bell Houston receives, in these exact words: everybody in the
    // story was told except the person deciding.
    //
    // A STOCK TRANSFER STILL SAYS NOTHING, and that is not an oversight: its
    // "ready" was never made, so a sentence about it would be untrue. `made` is
    // 0 for any mode but `make`, which is what keeps that true.
    // #705 -- "The 1 already sent still arrive and are counted in" read wrong.
    consequence: (sent === 1
      ? `The 1 already sent still arrives and is counted in at ${asking}.`
      // #729, Session 79.22b.6e -- the asking branch by name, not "here".
      : `The ${sent} already sent still arrive and are counted in at ${asking}.`)
      + ` The other ${left} will not be sent.`
      + (made > 0 ? ` The ${made} already made ${made === 1 ? 'stays' : 'stay'} on ${supplier}'s shelf.` : '')
      + ` ${supplier} will be told.` + alsoTold,
    who: supplier, alsoToldName: forSomeoneElse ? (askerName || null) : null, reasonLabel: `Why? ${supplier} will see this.`, blockedReason: `Say why, so ${supplier} ${knowsVerb(supplier)}.`,
    keepLabel: 'Keep waiting', submitLabel: 'Stop the rest',
  };
}

export async function shipTransfer(transfer, lines, { branchNames = {} } = {}) {
  const payload = lines
    .filter(l => Number(l.qty_shipped) > 0)
    .map(l => ({
      item_id: l.item_id,
      qty_shipped: Number(l.qty_shipped),
      unit_cost: l.unit_cost == null ? null : Number(l.unit_cost),
      // How this line was actually filled -- made for the order, or taken off
      // the sending branch's own shelf. It decides whether a production row is
      // written at all, so a wrong answer here either invents manufacturing
      // that never happened or leaves the maker's count too high.
      sourced: l.sourced || null,
    }));
  if (payload.length === 0) return { error: new Error('nothing to send') };

  const { error } = await supabase.rpc('ship_branch_transfer', {
    p_transfer_id: transfer.id,
    p_lines: payload,
  });
  if (error) return { error };

  // #514 / gate D30, Session 79.7 — THE REQUESTER'S SHIPPED BELL IS GONE, and
  // it is only safe to remove because D28 shipped in the same session.
  //
  // The requester used to get four bells: approved, denied, shipped, received.
  // D30 cuts that to two — once when somebody commits to it, once when it
  // lands. The approval bell carries the promised date, which is the thing that
  // actually changes their week, and a denial obviously stays. Shipped is the
  // one that goes.
  //
  // WHAT MAKES IT SAFE IS THAT THEY NOW HAVE SOMEWHERE TO LOOK. Reducing
  // notifications without that is just removing information: before D28 put the
  // play-by-play on My requests, this bell was the ONLY way a requester learned
  // their material had left. Now the card says so the moment it happens, with
  // how many actually shipped — which the bell never carried.
  //
  // The precedent is #500 at 79.1: a write-off notified four managers including
  // one who runs a different building, and the fix was to narrow the audience
  // rather than to add a filter. The risk is asymmetric and worth stating: too
  // few notifications means somebody checks a page; too many means the bell
  // stops being read at all, and then the DENIAL is missed along with
  // everything else.
  //
  // The receiving branch's managers still get it, and that is the point of the
  // message — they are the ones who need to prepare for a delivery.
  const receivers = await getRequestApproverIds(transfer.to_branch_id, null);
  const audience = [...new Set(receivers.filter(Boolean))];
  await sendNotification(
    audience,
    'Material on its way',
    `${transferLabel(transfer, branchNames)} has left`,
    'transfer_shipped',
    'inventory',
    'transfers',
    transfer.id,
  );
  return { ok: true };
}

export async function receiveTransfer(transfer, lines, { branchNames = {} } = {}) {
  const payload = lines
    .filter(l => Number(l.qty_received) > 0)
    .map(l => ({
      item_id: l.item_id,
      qty_received: Number(l.qty_received),
      new_avg_cost: l.new_avg_cost == null ? null : Number(l.new_avg_cost),
    }));
  if (payload.length === 0) return { error: new Error('nothing booked in') };

  const { error } = await supabase.rpc('receive_branch_transfer', {
    p_transfer_id: transfer.id,
    p_lines: payload,
  });
  if (error) return { error };

  const senders = await getRequestApproverIds(transfer.from_branch_id, null);
  const audience = [...new Set([transfer.requested_by, ...senders].filter(Boolean))];
  await sendNotification(
    audience,
    'Transfer received',
    `${transferLabel(transfer, branchNames)} was booked in`,
    'transfer_received',
    'inventory',
    'transfers',
    transfer.id,
  );
  return { ok: true };
}

// Gate D11 and D18. NO STOCK MOVES — the sender is already down the full
// shipped quantity by D6, so a write-off here would take the goods off them
// TWICE. What this records is that the balance is never arriving, with a reason
// and a value.
//
// Both branches' branch managers and the asking branch's approving managers,
// by bell AND by email, and no admins -- #683, Colby's ruling. (It was both
// branches' managers AND admins until 79.22b.4.3.) No preference toggle, by
// design: stock was lost and the message carries its value.
export async function closeTransferShort(transfer, reason, { branchNames = {} } = {}) {
  // #677 -- the figure this function RETURNS is not read: it values the loss
  // from the transfer line, which has held no cost since split loads. The value
  // is worked out below by shortfall(), the rule the transfer's page reads.
  const { error } = await supabase.rpc('close_branch_transfer_short', {
    p_transfer_id: transfer.id,
    p_reason: reason,
  });
  if (error) return { error };

  // Read AFTER the close: the counts are final now, and the trucks' costs were
  // frozen when they left. A failed read costs the message its figure, never
  // the close -- the write-off has already happened.
  const [linesRead, loadsRead] = await Promise.all([
    supabase.from('branch_transfer_items')
      .select('item_id, qty_shipped, qty_received, unit_cost_at_transfer')
      .eq('transfer_id', transfer.id),
    supabase.from('branch_transfer_loads')
      .select('status, from_branch_id, to_branch_id, branch_transfer_load_items(item_id, qty_loaded, qty_arrived, unit_cost_at_transfer)')
      .eq('transfer_id', transfer.id),
  ]);
  if (linesRead?.error || loadsRead?.error) {
    console.error('[#677] could not read the write-off\'s trucks to value it', {
      transferId: transfer.id, lines: linesRead?.error, loads: loadsRead?.error,
    });
  }
  const lost = shortfall(linesRead?.data || [], {
    loads: loadsRead?.data || [], fromBranchId: transfer.from_branch_id, toBranchId: transfer.to_branch_id,
  });
  // Stated only when every missing unit is priced -- the page's own rule, so
  // the bell never carries a figure the document declines to show.
  const value = !lost.anyUnpriced && lost.value > 0 ? lost.value : 0;

  // #683 -- ONE LIST, FOR THE BELL AND THE EMAIL. Colby's ruling at 79.22b.3.2:
  // "the branch managers and approving managers get both. no admin." The
  // supplying branch's approvers with no department are its branch managers;
  // askingApproverIds() is #649's lookup, the asking branch's branch managers
  // AND the department manager who may approve the asker's request -- the
  // person who agreed to it hears it went missing. The email is handed this
  // exact list, so the two can no longer be looked up apart: at K10 the bell
  // reached 5 people and the email 11.
  const [supplying, asking] = await Promise.all([
    getRequestApproverIds(transfer.from_branch_id, null),
    askingApproverIds(transfer),
  ]);
  const audience = [...new Set([...(supplying || []), ...(asking || [])].filter(Boolean).map(String))];

  const money = Number(value) > 0
    ? ` — about ${Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} of material`
    : '';
  await sendNotification(
    audience,
    'Transfer closed short',
    `${transferLabel(transfer, branchNames)} did not fully arrive${money}. ${reason}`,
    'transfer_closed_short',
    'inventory',
    'transfers',
    transfer.id,
  );

  // #514 / gate D29 -- the second and last email a transfer sends. Emailed
  // rather than belled because it carries a money figure and reaches people who
  // are not in the app that day, which is the same reason this notification has
  // no preference toggle: stock was lost and the message carries its value.
  await notifyTransferClosedShort({
    recipientIds: audience,
    label: transferLabel(transfer, branchNames),
    fromBranchName: branchNames?.[String(transfer.from_branch_id)] || 'a branch',
    toBranchName: branchNames?.[String(transfer.to_branch_id)] || 'a branch',
    shortValue: value,
    reason,
    transferId: transfer.id,
  });
  return { ok: true, value };
}

// D67, Colby: different things have different lead times and some of it is
// already on the shelf. A line's own promised date wins; otherwise the one date
// the approver set for the whole ask. Most transfers will only ever have that.
//
// #618 / #637, Session 79.22b -- MOVED HERE FROM transferLoads.js, which
// re-exports it, so the requester's card and the supplying branch's screens
// read ONE rule. That file imports this one, so the rule could not live there
// and be read here without a circle.
export function promisedDateFor(line, transfer) {
  return line?.promised_date || transfer?.confirmed_date || null;
}

// ── #649, Session 79.22b — THE ASKING BRANCH'S APPROVERS, WITH THE ASKER'S
// DEPARTMENT ─────────────────────────────────────────────────────────────────
//
// A null department means "this request has no department", which reaches the
// branch manager alone. Three bells to the asking branch passed a literal null
// - material made, a load arriving, nothing more coming - so the department
// manager who approved stage one never heard any of them, while the refusal
// path (denyTransfer) has always passed the department. One lookup,
// so the next bell to the asking branch cannot choose between the two again.
// Branch managers are still in it; a transfer with no department still reaches
// them (the off-catalog shape #501 records).
export async function askingApproverIds(transfer) {
  if (!transfer?.to_branch_id) return [];
  return getRequestApproverIds(transfer.to_branch_id, transfer.requested_by_department_id ?? null);
}

// ── #514 / gate D28 — the requester's own transfers, for My requests ─────────
//
// Colby, 79.6: "the play by play for the transfer housed in the requester's my
// requests page … let material requests and transfer request info live on the
// same page."
//
// SCOPED BY REQUESTER AND BY NOTHING ELSE, which is the same single `.eq` that
// makes My requests safe to give every role. No branch predicate and no status
// filter: a person's own requests are theirs wherever they raised them, and a
// status filter is precisely what made the Requests inbox useless to a
// requester — the defect #391 exists to fix.
export async function fetchMyTransfers(userId) {
  if (!userId) return { data: [], error: null };
  return supabase
    .from('branch_transfers')
    .select('*, branch_transfer_items(*, inventory_items(id, name, sku, uom))')
    .eq('requested_by', userId)
    .order('requested_at', { ascending: false });
}

// Shapes a transfer like a material request so My requests can render both from
// one list. THE NORMALISING HAPPENS HERE RATHER THAN IN THE PAGE, deliberately:
// the page already knows one document's column names and teaching it a second
// set is how two screens drift apart.
//
// THE ID IS PREFIXED, and that is the one line that must not be simplified.
// `branch_transfers` and `material_requests` BOTH NUMBER FROM 1 — gate decision
// D20 is about exactly this collision, where a transfer notification would have
// opened the inventory item with the same id. Here it would be a React key
// collision and a selection that highlights the wrong card: silent, plausible,
// and only visible if two rows happen to share a number.
export function asRequestRow(t) {
  const items = t.branch_transfer_items || [];
  const sum = (k) => items.reduce((n, li) => n + (Number(li[k]) || 0), 0);
  // #618 / #637, Session 79.22b -- EACH ITEM'S OWN PROMISE (gate 517 D67).
  // This read the transfer's date alone, so an item Houston promised LATER hid
  // behind an earlier one: "Houston promised Oct 14" for frames coming on the
  // 20th. The card now carries the earliest and the latest, and how many items
  // come after the date asked for, so its first line is true on its own. A
  // refused item is not coming at all - it is never lent the transfer's date.
  const coming = items.filter(li => li.line_status !== 'refused');
  const promised = coming.map(li => promisedDateFor(li, t)).filter(Boolean).sort();
  const lateItems = t.needed_by
    ? coming.filter(li => { const p = promisedDateFor(li, t); return !!p && p > t.needed_by; }).length
    : 0;
  return {
    __kind: 'transfer',
    __transferId: t.id,
    __mode: t.fulfilment_mode,
    // A transfer with no lines at all keeps the old reading rather than none.
    __promisedDate: promised[0] || (items.length ? null : (t.confirmed_date || null)),
    __promisedLast: promised[promised.length - 1] || null,
    __lateItems: lateItems,
    __neededBy: t.needed_by || null,
    __qtyAsked: sum('qty_requested'),
    __qtyShipped: sum('qty_shipped'),
    // #580 part (a), gate 553 F2 -- a request for the branch's own shelf never
    // ships: its tracker reads Asked · Confirmed · Being made · On the shelf,
    // and what it has is MADE rather than sent.
    __own: isOwnShelf(t),
    __qtyReady: sum('qty_ready'),
    __fromBranchId: t.from_branch_id,
    __toBranchId: t.to_branch_id,
    // #590 -- the transfer itself, because withdrawing it needs what the
    // flattened row drops (the requester's department, the maker's department,
    // every line's made and sent), and a second fetch would be a second copy.
    __raw: t,
    cancelled_by: t.cancelled_by || null,
    cancelled_at: t.cancelled_at || null,
    cancel_reason: t.cancel_reason || null,
    unfilled_reason: t.unfilled_reason || null,
    closed_by_branch_id: t.closed_by_branch_id ?? null,

    id: `t${t.id}`,
    request_number: t.transfer_number || 'Not yet numbered',
    status: t.status,
    created_at: t.requested_at,
    updated_at: t.updated_at,
    work_order_ref: t.work_order_ref || null,
    notes: t.notes || null,
    denial_reason: t.denial_reason || t.short_close_reason || null,
    denial_reason_label: null,
    // The branch being ASKED, because that is who the requester is waiting on.
    branch_id: t.from_branch_id,
    approved_by: t.approved_by || null,
    material_request_items: items.map(li => ({
      id: `t${t.id}-${li.id}`,
      item_id: li.item_id,
      qty_requested: li.qty_requested,
      qty_received: li.qty_received,
      qty_shipped: li.qty_shipped,
      notes: li.notes,
      is_off_catalog: false,
      inventory_items: li.inventory_items,
      // #618 / #637 -- read by the WHAT YOU ASKED FOR list, Colby's ruling for
      // where each item's date goes: beside the item, as Houston entered it.
      __refused: li.line_status === 'refused',
      __promisedDate: li.line_status === 'refused' ? null : promisedDateFor(li, t),
      // #572, Session 79.22b.4.7.1 -- the line itself, so the page can say
      // WHOSE refusal it was through the one rule, refusedByAsker(), and quote
      // its reason: an item the asking branch turned down is not the sending
      // branch's "not sending this".
      __line: li,
    })),
  };
}

// ── #608 / GATE 675 D15-D18, Session 79.22b.5.3 -- TRANSFERS ARE READ IN
// BOUNDED SETS, NEVER ALL AT ONCE ──────────────────────────────────────────
//
// The database hands back at most 1,000 rows and cuts the OLDEST first, with
// no error. A screen that read every transfer would quietly stop showing
// history the day the company passed that line, and nothing on it would look
// wrong. So:
//
//   transfersInHand()    the work in hand (D15): every open transfer, and a
//                        "Nothing more coming" one while a truck of it is still
//                        out. ONE rule, in the database (branch_transfers_in_
//                        hand()), read by the Transfers page, Stock Home's "on
//                        its way" and the driver's work row, so the three
//                        cannot disagree. Returns the query, so each reader
//                        adds its own select columns, filters and order.
//   fetchFinishedPage()  one page of Finished (D16), most recently finished
//                        first, with the total.
//   fetchTransferById()  one transfer, however old - what a link opens (D18).
//
// Every list read asks for its count, and cutOff() (readLimit.js) says when
// more exist than arrived (D17): a cut list says so, never passes for the whole.
//
// fetchTransfers() WAS HERE AND IS GONE (#608's amendment, ruled R1 at
// 79.22b.5.3): it read every transfer in the company and nothing called it.
export function transfersInHand(select = '*') {
  return supabase.rpc('branch_transfers_in_hand', {}, { count: 'exact' }).select(select);
}

// The row the page and a card need: the transfer and its lines.
const TRANSFER_ROW = '*, branch_transfer_items(*)';

// `term` (gate 675 D1, Session 79.22b.5.4): a search typed on any tab reaches
// every finished transfer in the database, not only the page on screen. Sent
// only when there is one, so the plain page asks exactly what it always has.
// Each row then carries what matched (`__match`), for the card's Matched line.
// `filters` (gate 675 D9, D11, Session 79.22b.5.4b): the database's own
// p_from, p_to, p_ended, p_since and p_until, as finishedArgs() in
// transferSortFilter.js builds them - only those that narrow are sent.
export async function fetchFinishedPage({ branchIds = null, limit = 25, offset = 0, order = 'recent', term = null, filters = null } = {}) {
  const { data: page, error } = await supabase.rpc('find_branch_transfers', {
    p_scope: 'finished', p_branch_ids: branchIds, p_order: order, p_limit: limit, p_offset: offset,
    ...(term ? { p_term: term } : {}),
    ...(filters || {}),
  });
  if (error) return { rows: [], total: 0, error };
  const found = page || [];
  const total = found.length ? Number(found[0].total_count) || 0 : 0;
  if (!found.length) return { rows: [], total, error: null };
  const ids = found.map(r => r.transfer_id);
  const { data: rows, error: rowsErr } = await supabase.from('branch_transfers').select(TRANSFER_ROW).in('id', ids);
  if (rowsErr) return { rows: [], total, error: rowsErr };
  // In the page's own order, each carrying the day it finished (D11's rule,
  // worked out once in the database) for the card to show (D16).
  const byId = new Map((rows || []).map(t => [String(t.id), t]));
  return {
    rows: found
      .map(r => (byId.has(String(r.transfer_id))
        ? {
          ...byId.get(String(r.transfer_id)),
          __endedAt: r.ended_at,
          __match: r.matched_on
            ? { transferId: r.transfer_id, loadId: r.load_id ?? null, matchedOn: r.matched_on, matchedValue: r.matched_value || null }
            : null,
        }
        : null))
      .filter(Boolean),
    total,
    error: null,
  };
}

// ── #752, Session 79.22b.5.4b -- REFUSALS STILL WAITING FOR THE ASKING
// BRANCH'S DECISION, read on their own so Finished's pages never hide one ──────
//
// Drawing A, approved by Colby (R2 at 79.22b.5.4): a short "Waiting for {branch}
// to decide" group pinned at the top of Finished, never paged. A refusal is
// work for the asking branch's approving manager, and gate 675 D15's rule is
// that work is never hidden behind a page.
//
// A BOUNDED READ, NO DATABASE CHANGE. The candidates: finished transfers, not
// withdrawn, past the asking branch's own approval (#571), with no decision
// recorded, carrying at least one refused item - one hop to the items, which
// PostgREST filters honestly (never two). `branchIds` are the ASKING branches
// in view (to_branch_id); null for every branch. The page then keeps only what
// refusalAwaitsDecision() in transferLoads.js says still waits - the one rule
// the transfer sheet's routing panel reads (#753) - once withSuccessors() has
// marked the older decisions that left only a successor document.
//
// `cut` says the candidate read reached its bound (D17: a bounded read says
// so). WAITING_REFUSALS_READ is far above anything a branch would carry.
export const WAITING_REFUSALS_READ = 200;
const WAITING_STATUSES = ['received', 'denied', 'closed_short', 'closed_unfilled'];
export async function fetchWaitingRefusals({ branchIds = null } = {}) {
  let q = supabase
    .from('branch_transfers')
    .select('id, branch_transfer_items!inner(line_status)', { count: 'exact' })
    .in('status', WAITING_STATUSES)
    .is('refusal_decision', null)
    .not('endorsed_at', 'is', null)
    .eq('branch_transfer_items.line_status', 'refused');
  if (Array.isArray(branchIds)) q = q.in('to_branch_id', branchIds);
  const { data: found, error, count } = await q.order('id', { ascending: false }).limit(WAITING_REFUSALS_READ);
  if (error) return { rows: [], cut: false, error };
  const ids = (found || []).map(r => r.id);
  if (!ids.length) return { rows: [], cut: false, error: null };
  const { data: rows, error: rowsErr } = await supabase.from('branch_transfers').select(TRANSFER_ROW).in('id', ids);
  if (rowsErr) return { rows: [], cut: false, error: rowsErr };
  return { rows: rows || [], cut: (Number(count) || 0) > ids.length, error: null };
}

// The order the group lists them in, as the approved drawing does: the most
// recently refused first. When an item was refused is its own refused_at
// (#698), else the transfer's refusal stamp (#573); neither for the oldest.
export function refusedAtOf(transfer) {
  const fromItems = (transfer?.branch_transfer_items || [])
    .filter(l => l?.line_status === 'refused' && l.refused_at)
    .map(l => l.refused_at)
    .sort();
  return fromItems.length ? fromItems[fromItems.length - 1] : (transfer?.denied_at || transfer?.approved_at || null);
}
export function waitingOrder(list) {
  return [...(Array.isArray(list) ? list : [])].sort((a, b) =>
    (new Date(refusedAtOf(b) || 0) - new Date(refusedAtOf(a) || 0)) || (Number(b.id) - Number(a.id)));
}

export async function fetchTransferById(id) {
  if (id == null) return { data: null, error: null };
  return supabase.from('branch_transfers').select(TRANSFER_ROW).eq('id', id).maybeSingle();
}
